import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  TrendingUp,
  Plus,
  Pencil,
  Trash2,
  Tag,
  DollarSign,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Check,
  Loader2,
  Circle,
  CheckCircle2,
  CalendarDays,
  BarChart3,
  Calendar,
  Search,
  Activity,
  User,
  Building2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { PieChart, Pie, Cell, Tooltip as ReTooltip, ResponsiveContainer } from "recharts";
import { getHolidayName } from "../utils/holidays";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ExpenseCategory {
  id: string;
  name: string;
  description?: string;
  color?: string;
  goalPercentage?: number;
  isActive: boolean;
}

interface Expense {
  id: string;
  name: string;
  amount: number;
  categoryId?: string;
  category?: ExpenseCategory;
  frequency: "daily" | "weekly" | "monthly" | "quarterly" | "yearly" | "one-time";
  billDay?: number;
  billMonth?: number;
  startDate?: string;
  endDate?: string;
  notes?: string;
  isActive: boolean;
}

interface RevenueItem {
  customerId: string;
  customerName: string;
  amount: number;
  type: "actual" | "projected";
  serviceType?: string;
  revenueType?: "inbound" | "reactivation" | "initial_payment";
  revenueSubtype?: "minimum" | "overage" | "weekly_overage" | "weekly_overage_catchup";
  periodStart?: string;
  periodEnd?: string;
}

interface AdditionalIncomeItem {
  id: string;
  label: string;
  amount: number;
  notes?: string;
}

interface ScheduledExpenseItem {
  id: string;
  name: string;
  amount: number;
  categoryName: string | null;
  categoryColor: string | null;
  frequency: string;
  source?: "payroll";
}

interface UpcomingOccurrence {
  date: string;
  amount: number;
  isOverridden: boolean;
  originalDate?: string;
}

interface DayData {
  date: string; // "YYYY-MM-DD"
  dayOfWeek: number; // 0=Sun
  revenue: RevenueItem[];
  additionalIncome: AdditionalIncomeItem[];
  expenses: ScheduledExpenseItem[];
}

interface DayRow extends DayData {
  totalRevenue: number;
  totalExpenses: number;
  dailyNet: number;
  runningBalance: number;
}

interface BreakdownExpenseItem {
  id: string;
  name: string;
  originalAmount: number;
  frequency: string;
  monthlyAmount: number;
}

interface BreakdownCategory {
  id: string | null;
  name: string;
  color: string | null;
  goalPercentage: number | null;
  monthlyAmount: number;
  expenses: BreakdownExpenseItem[];
}

interface PayrollMember {
  id: string;
  name: string;
  rateType: string;
  monthlyAmount: number;
}

interface PayrollRoleBucket {
  role: string;
  memberCount: number;
  monthlyAmount: number;
  members: PayrollMember[];
}

type PayrollGroupKey = "outbound" | "inbound" | "admin_support";

interface PayrollGroup {
  key: PayrollGroupKey;
  label: string;
  monthlyAmount: number;
  goalPercentage: number | null;
  roles: PayrollRoleBucket[];
}

interface BreakdownData {
  year: number;
  month: number;
  daysInMonth: number;
  totalExpenses: number;
  totalPayroll: number;
  categories: BreakdownCategory[];
  payrollGroups: PayrollGroup[];
  netProfitGoalPercentage: number | null;
}

const FREQUENCY_LABELS: Record<Expense["frequency"], string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
  "one-time": "One-Time",
};

const FREQUENCY_ORDER: Expense["frequency"][] = [
  "monthly", "weekly", "yearly", "quarterly", "daily", "one-time",
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function billScheduleLabel(expense: Expense): string {
  switch (expense.frequency) {
    case "daily":     return "Every day";
    case "one-time":  return expense.startDate ? `On ${expense.startDate}` : "One-time";
    case "weekly":    return expense.billDay != null ? `Every ${DAY_OF_WEEK[expense.billDay]}` : "Weekly";
    case "monthly":   return expense.billDay ? `${ordinal(expense.billDay)} of each month` : "Monthly";
    case "quarterly":
      return expense.billDay && expense.billMonth
        ? `${ordinal(expense.billDay)} of ${MONTHS[(expense.billMonth - 1) % 12]} (quarterly)`
        : "Quarterly";
    case "yearly":
      return expense.billDay && expense.billMonth
        ? `${MONTHS[expense.billMonth - 1]} ${ordinal(expense.billDay)}`
        : "Yearly";
    default: return expense.frequency;
  }
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function fmt(amount: number): string {
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// ─── Exclusion key helpers ─────────────────────────────────────────────────────
const revKey = (date: string, customerId: string, revenueType?: string, revenueSubtype?: string) =>
  `rev:${date}:${customerId}:${revenueType ?? "inbound"}${revenueSubtype ? `:${revenueSubtype}` : ""}`;
const incKey = (date: string, id: string) => `inc:${date}:${id}`;
const expKey = (date: string, id: string) => `exp:${date}:${id}`;

function nextDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
}

function computeRows(days: DayData[], startingBalance: number, excluded: Set<string> = new Set()): DayRow[] {
  let balance = startingBalance;
  return days.map((day) => {
    const billingRevenue = day.revenue
      .filter((r) => !excluded.has(revKey(day.date, r.customerId, r.revenueType, r.revenueSubtype)))
      .reduce((s, r) => s + r.amount, 0);
    const additionalRevenue = (day.additionalIncome || [])
      .filter((a) => !excluded.has(incKey(day.date, a.id)))
      .reduce((s, a) => s + a.amount, 0);
    const totalRevenue = billingRevenue + additionalRevenue;
    const totalExpenses = day.expenses
      .filter((e) => !excluded.has(expKey(day.date, e.id)))
      .reduce((s, e) => s + e.amount, 0);
    const dailyNet = totalRevenue - totalExpenses;
    balance += dailyNet;
    return { ...day, totalRevenue, totalExpenses, dailyNet, runningBalance: balance };
  });
}

// ─── Week grouping helpers ─────────────────────────────────────────────────────

interface WeekGroup {
  key: string;         // YYYY-MM-DD of the Monday
  label: string;       // "Mar 3 – 9" or "Mar 31 – Apr 6"
  days: DayRow[];
  totalRevenue: number;
  totalExpenses: number;
  netFlow: number;
  openingBalance: number;
  closingBalance: number;
}

/** Returns the YYYY-MM-DD of the Monday of the week containing dateStr. */
function getMondayKey(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  const dow = d.getDay(); // 0=Sun
  d.setDate(d.getDate() - ((dow + 6) % 7));
  return d.toISOString().split("T")[0];
}

function formatWeekLabel(from: Date, to: Date): string {
  const from_day = from.getDate();
  const to_day   = to.getDate();
  if (from.getMonth() === to.getMonth()) {
    return `${MONTHS[from.getMonth()]} ${from_day} – ${to_day}`;
  }
  return `${MONTHS[from.getMonth()]} ${from_day} – ${MONTHS[to.getMonth()]} ${to_day}`;
}

function groupByWeeks(rows: DayRow[]): WeekGroup[] {
  if (rows.length === 0) return [];
  const weekMap = new Map<string, DayRow[]>();
  const weekOrder: string[] = [];
  for (const row of rows) {
    const key = getMondayKey(row.date);
    if (!weekMap.has(key)) {
      weekMap.set(key, []);
      weekOrder.push(key);
    }
    weekMap.get(key)!.push(row);
  }
  return weekOrder.map((key) => {
    const days = weekMap.get(key)!;
    const totalRevenue  = days.reduce((s, d) => s + d.totalRevenue, 0);
    const totalExpenses = days.reduce((s, d) => s + d.totalExpenses, 0);
    const netFlow        = totalRevenue - totalExpenses;
    const openingBalance = days[0].runningBalance - days[0].dailyNet;
    const closingBalance = days[days.length - 1].runningBalance;
    const firstDate      = new Date(days[0].date + "T00:00:00");
    const lastDate       = new Date(days[days.length - 1].date + "T00:00:00");
    return { key, label: formatWeekLabel(firstDate, lastDate), days, totalRevenue, totalExpenses, netFlow, openingBalance, closingBalance };
  });
}

// ─── Blank form state ─────────────────────────────────────────────────────────

const blankExpense = (): Partial<Expense> => ({
  name: "",
  amount: 0,
  frequency: "monthly",
  billDay: undefined,
  billMonth: undefined,
  categoryId: undefined,
  startDate: undefined,
  endDate: undefined,
  notes: "",
  isActive: true,
});

const blankCategory = () => ({ name: "", description: "", color: "#3B82F6" });

// ─── API helpers ──────────────────────────────────────────────────────────────

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("authToken")}`, "Content-Type": "application/json" };
}

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`/api/v1${path}`, { ...options, headers: authHeaders() });
  const json = await res.json();
  if (!json.success) throw new Error(json.error || "Request failed");
  return json.data;
}

// ─── Component ────────────────────────────────────────────────────────────────

// ─── Expense Breakdown Section ────────────────────────────────────────────────

const BD_COLS = "grid grid-cols-[160px_1fr_112px_64px_64px_80px] gap-3 items-center";

function averageBreakdownData(results: BreakdownData[]): BreakdownData {
  const n = results.length;
  if (n === 0) throw new Error("No data");
  if (n === 1) return results[0];

  const totalExpenses = results.reduce((s, r) => s + r.totalExpenses, 0) / n;
  const totalPayroll  = results.reduce((s, r) => s + r.totalPayroll, 0) / n;

  // Average categories by ID, and their expense items by ID
  const catMap = new Map<string | null, { base: BreakdownCategory; sum: number; expSums: Map<string, { base: BreakdownExpenseItem; sum: number }> }>();
  for (const result of results) {
    for (const cat of result.categories) {
      if (!catMap.has(cat.id)) catMap.set(cat.id, { base: cat, sum: 0, expSums: new Map() });
      const entry = catMap.get(cat.id)!;
      entry.sum += cat.monthlyAmount;
      for (const exp of cat.expenses) {
        if (!entry.expSums.has(exp.id)) entry.expSums.set(exp.id, { base: exp, sum: 0 });
        entry.expSums.get(exp.id)!.sum += exp.monthlyAmount;
      }
    }
  }
  const categories: BreakdownCategory[] = [];
  for (const [, { base, sum, expSums }] of catMap) {
    const expenses = [...expSums.values()].map(({ base: b, sum: s }) => ({ ...b, monthlyAmount: s / n }));
    categories.push({ ...base, monthlyAmount: sum / n, expenses });
  }

  // Average payroll by group, then by role and member within each group
  const groupMap = new Map<PayrollGroupKey, { base: PayrollGroup; sum: number; roleSums: Map<string, { base: PayrollRoleBucket; sum: number; memberSums: Map<string, { base: PayrollMember; sum: number }> }> }>();
  for (const result of results) {
    for (const group of result.payrollGroups) {
      if (!groupMap.has(group.key)) groupMap.set(group.key, { base: group, sum: 0, roleSums: new Map() });
      const gEntry = groupMap.get(group.key)!;
      gEntry.sum += group.monthlyAmount;
      for (const role of group.roles) {
        if (!gEntry.roleSums.has(role.role)) gEntry.roleSums.set(role.role, { base: role, sum: 0, memberSums: new Map() });
        const rEntry = gEntry.roleSums.get(role.role)!;
        rEntry.sum += role.monthlyAmount;
        for (const m of role.members) {
          if (!rEntry.memberSums.has(m.id)) rEntry.memberSums.set(m.id, { base: m, sum: 0 });
          rEntry.memberSums.get(m.id)!.sum += m.monthlyAmount;
        }
      }
    }
  }
  const payrollGroups: PayrollGroup[] = [];
  for (const [, { base, sum, roleSums }] of groupMap) {
    const roles: PayrollRoleBucket[] = [];
    for (const [, { base: rBase, sum: rSum, memberSums }] of roleSums) {
      const members = [...memberSums.values()].map(({ base: b, sum: s }) => ({ ...b, monthlyAmount: s / n }));
      roles.push({ ...rBase, monthlyAmount: rSum / n, members });
    }
    payrollGroups.push({ ...base, monthlyAmount: sum / n, roles });
  }

  return { ...results[0], totalExpenses, totalPayroll, categories, payrollGroups };
}

function ExpenseBreakdownSection({
  monthlyRev,
  avgMonths,
  avgLabel,
  title = "Expense Breakdown",
  subtitlePrefix = "Avg monthly cost",
  headerExtra,
  compareData,
  compareRevBase,
  onDataLoaded,
  revenueLoading = false,
}: {
  monthlyRev: number | null;
  avgMonths: { year: number; month: number }[];
  avgLabel: string;
  title?: string;
  subtitlePrefix?: string;
  headerExtra?: React.ReactNode;
  /** The 12-month average breakdown to diff each row against — only passed by the single-month section. */
  compareData?: BreakdownData | null;
  compareRevBase?: number | null;
  onDataLoaded?: (data: BreakdownData | null, revBase: number | null) => void;
  /** True while the parent's own revenue fetch (monthlyRev) is still in flight — kept in the
   * loading state until this clears too, so the Total/Net Profit rows (which need revenue to
   * compute their %) don't pop in a moment after the rest of the table already rendered. */
  revenueLoading?: boolean;
}) {
  const [data, setData]         = useState<BreakdownData | null>(null);
  const [loading, setLoading]   = useState(true);
  const [expandedRows, setExpandedRows]   = useState<Set<string>>(new Set());
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  // "undefined" means "not editing anything" — deliberately distinct from `null`, since the
  // synthetic "Not Assigned" category itself has `id: null`. Using `null` as the empty state
  // used to make `editingGoal === cat.id` true for that row by coincidence on every render,
  // permanently stuck showing an open (and auto-focused) edit box nobody clicked into.
  const [editingGoal, setEditingGoal]       = useState<string | null | undefined>(undefined);
  const [goalInputVal, setGoalInputVal]     = useState("");
  const [editingPayrollGoal, setEditingPayrollGoal] = useState<PayrollGroupKey | null>(null);
  const [payrollGoalInput, setPayrollGoalInput]     = useState("");
  const [editingNetProfitGoal, setEditingNetProfitGoal] = useState(false);
  const [netProfitGoalInput, setNetProfitGoalInput]     = useState("");

  // catId === null targets the synthetic "Not Assigned" row, saved via /row-goal instead of
  // /categories/:id since it has no real category to attach a goal to.
  const saveGoal = (catId: string | null, value: string) => {
    const trimmed = value.trim();
    const newVal: number | null | "invalid" =
      trimmed === ""
        ? null
        : (() => {
            const p = parseFloat(trimmed);
            return isNaN(p) || p < 0 || p > 100 ? "invalid" : Math.round(p * 10) / 10;
          })();

    setEditingGoal(undefined);
    if (newVal === "invalid") return;

    // Optimistic update
    setData((prev) =>
      prev
        ? { ...prev, categories: prev.categories.map((c) => c.id === catId ? { ...c, goalPercentage: newVal } : c) }
        : prev
    );
    if (catId) {
      apiFetch(`/cash-flow/categories/${catId}`, {
        method: "PATCH",
        body: JSON.stringify({ goalPercentage: newVal }),
      }).catch(() => {});
    } else {
      apiFetch(`/cash-flow/row-goal`, {
        method: "PATCH",
        body: JSON.stringify({ row: "not_assigned", goalPercentage: newVal }),
      }).catch(() => {});
    }
  };

  const saveNetProfitGoal = (value: string) => {
    const trimmed = value.trim();
    const newVal: number | null | "invalid" =
      trimmed === ""
        ? null
        : (() => {
            const p = parseFloat(trimmed);
            return isNaN(p) || p < 0 || p > 100 ? "invalid" : Math.round(p * 10) / 10;
          })();

    setEditingNetProfitGoal(false);
    if (newVal === "invalid") return;

    setData((prev) => (prev ? { ...prev, netProfitGoalPercentage: newVal } : prev));
    apiFetch(`/cash-flow/row-goal`, {
      method: "PATCH",
      body: JSON.stringify({ row: "net_profit", goalPercentage: newVal }),
    }).catch(() => {});
  };

  const savePayrollGoal = (group: PayrollGroupKey, value: string) => {
    const trimmed = value.trim();
    const newVal: number | null | "invalid" =
      trimmed === ""
        ? null
        : (() => {
            const p = parseFloat(trimmed);
            return isNaN(p) || p < 0 || p > 100 ? "invalid" : Math.round(p * 10) / 10;
          })();

    setEditingPayrollGoal(null);
    if (newVal === "invalid") return;

    setData((prev) =>
      prev
        ? { ...prev, payrollGroups: prev.payrollGroups.map((g) => g.key === group ? { ...g, goalPercentage: newVal } : g) }
        : prev
    );
    apiFetch("/cash-flow/payroll-goal", {
      method: "PATCH",
      body: JSON.stringify({ group, goalPercentage: newVal }),
    }).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    Promise.all(avgMonths.map((m) => apiFetch(`/cash-flow/expense-breakdown?year=${m.year}&month=${m.month}`)))
      .then((results: BreakdownData[]) => setData(averageBreakdownData(results)))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avgMonths.map((m) => `${m.year}-${m.month}`).join(",")]);

  useEffect(() => {
    if (onDataLoaded) onDataLoaded(data, monthlyRev && monthlyRev > 0 ? monthlyRev : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, monthlyRev]);

  const toggleRow  = (key: string) =>
    setExpandedRows(prev  => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  const toggleRole = (key: string) =>
    setExpandedRoles(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });

  const totalCombined = data ? data.totalExpenses + data.totalPayroll : 0;
  const revBase       = monthlyRev && monthlyRev > 0 ? monthlyRev : null;

  const pctOfRev = (amt: number) => (revBase ? (amt / revBase) * 100 : null);
  const fmtPct   = (n: number)   => n.toFixed(1) + "%";

  // Only populated for the single-month section — diffs this row's % of revenue against the
  // matching row in the 12-month average passed down as `compareData`/`compareRevBase`.
  const compareFor = (amount: number, matchAmount: number | undefined): { pctDiff: number; amtDiff: number } | null => {
    if (!compareData || compareRevBase == null || matchAmount === undefined) return null;
    const thisPct = pctOfRev(amount);
    const avgPct = (matchAmount / compareRevBase) * 100;
    if (thisPct == null) return null;
    return { pctDiff: thisPct - avgPct, amtDiff: amount - matchAmount };
  };

  const CompareLine = ({ cmp }: { cmp: { pctDiff: number; amtDiff: number } | null }) => {
    if (!cmp) return null;
    const isOver = cmp.amtDiff > 0;
    return (
      <p className={`text-[10px] mt-0.5 ${Math.abs(cmp.amtDiff) < 0.01 ? "text-gray-400" : isOver ? "text-red-500" : "text-emerald-600"}`}>
        vs avg: {cmp.amtDiff > 0 ? "+" : ""}{fmt(cmp.amtDiff)} ({cmp.pctDiff > 0 ? "+" : ""}{cmp.pctDiff.toFixed(1)}pp)
      </p>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Card header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
        <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-violet-50">
          <BarChart3 className="h-5 w-5 text-violet-600" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-400">
            {subtitlePrefix} · {avgLabel}
          </p>
        </div>
        {headerExtra}
        {!loading && data && (
          <div className="ml-auto flex items-center gap-6">
            {revBase != null && (
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-500">{fmt(revBase)}</p>
                <p className="text-xs text-gray-400">revenue used for %</p>
              </div>
            )}
            <div className="text-right">
              <p className="text-sm font-bold text-gray-900">{fmt(totalCombined)}</p>
              <p className="text-xs text-gray-400">total monthly costs</p>
            </div>
          </div>
        )}
      </div>

      {loading || revenueLoading ? (
        <div className="flex items-center gap-2 text-gray-400 px-6 py-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading breakdown…</span>
        </div>
      ) : !data ? (
        <p className="px-6 py-6 text-sm text-gray-400">Unable to load expense breakdown.</p>
      ) : (
        <>
          {/* Column headers */}
          <div className={`${BD_COLS} px-6 pt-3 pb-2 text-xs font-medium text-gray-400 uppercase tracking-wide`}>
            <span>Category</span>
            <span>Distribution</span>
            <span className="text-right">Monthly</span>
            <span className="text-right">% Rev</span>
            <span className="text-right flex items-center justify-end gap-1">Goal % <Pencil className="h-2.5 w-2.5" /></span>
            <span className="text-right">Over / Under</span>
          </div>

          <div className="pb-1">
            {/* ── Expense category rows ─────────────────────────────────── */}
            {data.categories.map((cat) => {
              const rowKey    = cat.id ?? "not-assigned";
              const isOpen    = expandedRows.has(rowKey);
              const hasItems  = cat.expenses.length > 0;
              const barPct    = totalCombined > 0 ? Math.min(100, (cat.monthlyAmount / totalCombined) * 100) : 0;
              const revPct    = pctOfRev(cat.monthlyAmount);
              const overUnder =
                cat.goalPercentage != null && revPct != null
                  ? revPct - cat.goalPercentage
                  : null;
              const color = cat.color ?? "#94A3B8";

              return (
                <div key={rowKey} className="border-b border-gray-50 last:border-b-0">
                  {/* Main row */}
                  <div
                    className={`${BD_COLS} px-6 py-3 transition-colors ${hasItems ? "cursor-pointer hover:bg-gray-50" : "hover:bg-gray-50"}`}
                    onClick={() => hasItems && toggleRow(rowKey)}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      {/* Chevron or spacer */}
                      <span className="flex-shrink-0 w-4 text-gray-400">
                        {hasItems ? (
                          isOpen
                            ? <ChevronUp className="h-3.5 w-3.5" />
                            : <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <span className="inline-block w-3.5" />
                        )}
                      </span>
                      <span
                        className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm font-medium text-gray-800 truncate">{cat.name}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${barPct}%`, backgroundColor: color }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-900 text-right">{fmt(cat.monthlyAmount)}</span>
                    <div className="text-right">
                      <span className="text-sm text-gray-500">
                        {revPct != null ? fmtPct(revPct) : "—"}
                      </span>
                      <CompareLine cmp={compareFor(cat.monthlyAmount, compareData?.categories.find((c) => c.id === cat.id)?.monthlyAmount)} />
                    </div>
                    {/* Goal % — inline editable for named categories */}
                    {editingGoal === cat.id ? (
                      <div
                        className="flex items-center justify-end gap-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          autoFocus
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={goalInputVal}
                          onChange={(e) => setGoalInputVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")  saveGoal(cat.id, goalInputVal);
                            if (e.key === "Escape") setEditingGoal(undefined);
                          }}
                          onBlur={() => saveGoal(cat.id, goalInputVal)}
                          className="w-12 text-xs text-right bg-white border border-violet-400 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
                        />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                    ) : (
                      <span
                        className={`text-sm text-right cursor-pointer hover:text-violet-600 transition-colors ${cat.goalPercentage != null ? "text-gray-500" : "text-gray-300"}`}
                        title="Click to set goal %"
                        onClick={(e) => {
                          // Always stop propagation here — this cell sits inside the row's own
                          // onClick (expand/collapse). Previously this returned early for the
                          // "Not Assigned" row (no cat.id) before reaching stopPropagation, so a
                          // click here fell through and toggled the row open/closed instead of
                          // doing nothing, while every other category correctly opened the goal
                          // editor. Not Assigned can now set a goal too (saved via /row-goal).
                          e.stopPropagation();
                          setEditingGoal(cat.id);
                          setGoalInputVal(cat.goalPercentage != null ? String(cat.goalPercentage) : "");
                        }}
                      >
                        {cat.goalPercentage != null ? fmtPct(cat.goalPercentage) : "—"}
                      </span>
                    )}
                    <div className={`text-right ${overUnder == null ? "text-gray-300" : overUnder > 0 ? "text-red-500" : "text-emerald-600"}`}>
                      {overUnder == null ? (
                        <span className="text-sm font-medium">—</span>
                      ) : (() => {
                        const goalAmt = revBase! * (cat.goalPercentage! / 100);
                        const diffAmt = cat.monthlyAmount - goalAmt;
                        return (
                          <>
                            <span className="text-sm font-medium block">{overUnder > 0 ? "+" : ""}{fmtPct(overUnder)}</span>
                            <span className="text-[10px] opacity-75">{diffAmt > 0 ? "+" : ""}{fmt(diffAmt)}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Expanded expense line items */}
                  {isOpen && (
                    <div className="bg-gray-50/60 border-t border-gray-100">
                      {cat.expenses.map((exp) => (
                        <div
                          key={exp.id}
                          className="flex items-center px-6 py-2 gap-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-100/50 transition-colors"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0 pl-9">
                            <span className="h-1.5 w-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                            <span className="text-xs text-gray-700 truncate">{exp.name}</span>
                            <span className="text-xs text-gray-400 flex-shrink-0">
                              · {FREQUENCY_LABELS[exp.frequency as Expense["frequency"]] ?? exp.frequency}
                            </span>
                          </div>
                          <span className="text-xs font-medium text-gray-600 text-right flex-shrink-0 w-28">
                            {fmt(exp.monthlyAmount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* ── Payroll rows: Outbound / Inbound / Administrative Support ───── */}
            {data.payrollGroups.filter((g) => g.monthlyAmount > 0).map((group) => {
              const rowKey    = `payroll-${group.key}`;
              const isOpen    = expandedRows.has(rowKey);
              const hasRoles  = group.roles.length > 0;
              const barPct    = totalCombined > 0 ? Math.min(100, (group.monthlyAmount / totalCombined) * 100) : 0;
              const revPct    = pctOfRev(group.monthlyAmount);
              const groupGoal = group.goalPercentage;
              const groupOverUnder = groupGoal != null && revPct != null ? revPct - groupGoal : null;

              return (
                <div key={group.key} className="border-b border-gray-50 last:border-b-0">
                  {/* Main group row */}
                  <div
                    className={`${BD_COLS} px-6 py-3 transition-colors ${hasRoles ? "cursor-pointer hover:bg-gray-50" : "hover:bg-gray-50"}`}
                    onClick={() => hasRoles && toggleRow(rowKey)}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="flex-shrink-0 w-4 text-gray-400">
                        {hasRoles ? (
                          isOpen
                            ? <ChevronUp className="h-3.5 w-3.5" />
                            : <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <span className="inline-block w-3.5" />
                        )}
                      </span>
                      <span className="h-2.5 w-2.5 rounded-full flex-shrink-0 bg-indigo-400" />
                      <span className="text-sm font-medium text-gray-800">{group.label}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-indigo-400 transition-all duration-500" style={{ width: `${barPct}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-900 text-right">{fmt(group.monthlyAmount)}</span>
                    <div className="text-right">
                      <span className="text-sm text-gray-500">
                        {revPct != null ? fmtPct(revPct) : "—"}
                      </span>
                      <CompareLine cmp={compareFor(group.monthlyAmount, compareData?.payrollGroups.find((g) => g.key === group.key)?.monthlyAmount)} />
                    </div>
                    {/* Goal % — inline editable */}
                    {editingPayrollGoal === group.key ? (
                      <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          autoFocus
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={payrollGoalInput}
                          onChange={(e) => setPayrollGoalInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter")  savePayrollGoal(group.key, payrollGoalInput);
                            if (e.key === "Escape") setEditingPayrollGoal(null);
                          }}
                          onBlur={() => savePayrollGoal(group.key, payrollGoalInput)}
                          className="w-12 text-xs text-right bg-white border border-violet-400 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
                        />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                    ) : (
                      <span
                        className={`text-sm text-right cursor-pointer hover:text-violet-600 transition-colors ${groupGoal != null ? "text-gray-500" : "text-gray-300"}`}
                        title="Click to set goal %"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingPayrollGoal(group.key);
                          setPayrollGoalInput(groupGoal != null ? String(groupGoal) : "");
                        }}
                      >
                        {groupGoal != null ? fmtPct(groupGoal) : "—"}
                      </span>
                    )}
                    {/* Over/Under */}
                    <div className={`text-right ${groupOverUnder == null ? "text-gray-300" : groupOverUnder > 0 ? "text-red-500" : "text-emerald-600"}`}>
                      {groupOverUnder == null ? (
                        <span className="text-sm font-medium">—</span>
                      ) : (() => {
                        const goalAmt = revBase! * (groupGoal! / 100);
                        const diffAmt = group.monthlyAmount - goalAmt;
                        return (
                          <>
                            <span className="text-sm font-medium block">{groupOverUnder > 0 ? "+" : ""}{fmtPct(groupOverUnder)}</span>
                            <span className="text-[10px] opacity-75">{diffAmt > 0 ? "+" : ""}{fmt(diffAmt)}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Expanded: role groups within this group */}
                  {isOpen && (
                    <div className="bg-gray-50/60 border-t border-gray-100">
                      {group.roles.map((role) => {
                        const roleKey      = `${group.key}:${role.role}`;
                        const roleOpen     = expandedRoles.has(roleKey);
                        const hasMembers   = role.members.length > 0;

                        return (
                          <div key={roleKey} className="border-b border-gray-100 last:border-b-0">
                            {/* Role group row */}
                            <div
                              className={`flex items-center px-6 py-2.5 gap-3 transition-colors ${hasMembers ? "cursor-pointer hover:bg-gray-100/60" : ""}`}
                              onClick={() => hasMembers && toggleRole(roleKey)}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0 pl-7">
                                <span className="flex-shrink-0 w-4 text-gray-400">
                                  {hasMembers ? (
                                    roleOpen
                                      ? <ChevronUp className="h-3 w-3" />
                                      : <ChevronDown className="h-3 w-3" />
                                  ) : (
                                    <span className="inline-block w-3" />
                                  )}
                                </span>
                                <span className="text-xs font-semibold text-gray-700 truncate">{role.role}</span>
                                <span className="text-xs text-gray-400 flex-shrink-0">
                                  · {role.memberCount} {role.memberCount === 1 ? "member" : "members"}
                                </span>
                              </div>
                              <span className="text-xs font-semibold text-gray-700 text-right flex-shrink-0 w-28">
                                {fmt(role.monthlyAmount)}
                              </span>
                            </div>

                            {/* Expanded: individual members */}
                            {roleOpen && (
                              <div className="bg-white/70 border-t border-gray-100">
                                {role.members.map((member) => (
                                  <div
                                    key={member.id}
                                    className="flex items-center px-6 py-2 gap-3 border-b border-gray-50 last:border-b-0"
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0 pl-16">
                                      <span className="h-1.5 w-1.5 rounded-full bg-indigo-200 flex-shrink-0" />
                                      <span className="text-xs text-gray-600 truncate">{member.name}</span>
                                      <span className="text-xs text-gray-400 flex-shrink-0 capitalize">
                                        · {member.rateType}
                                      </span>
                                    </div>
                                    <span className="text-xs text-gray-500 text-right flex-shrink-0 w-28">
                                      {fmt(member.monthlyAmount)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer totals row */}
          {revBase != null && (() => {
            // Net total of the Over/Under column — sums the $ diff (actual vs. goal) across
            // every category and payroll group that has a goal set, so a reader can see at a
            // glance whether spending overall is running over or under target, not just per row.
            const rowsWithGoals = [...data.categories, ...data.payrollGroups];
            let overUnderNetTotal: number | null = null;
            for (const row of rowsWithGoals) {
              if (row.goalPercentage == null) continue;
              const goalAmt = revBase * (row.goalPercentage / 100);
              overUnderNetTotal = (overUnderNetTotal ?? 0) + (row.monthlyAmount - goalAmt);
            }

            return (
              <div className={`${BD_COLS} px-6 py-3 bg-gray-50 border-t border-gray-100`}>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</span>
                <span />
                <span className="text-sm font-bold text-gray-900 text-right">{fmt(totalCombined)}</span>
                <span className="text-sm font-bold text-gray-700 text-right">
                  {fmtPct((totalCombined / revBase) * 100)}
                </span>
                <span />
                <div className={`text-right ${overUnderNetTotal == null ? "text-gray-300" : overUnderNetTotal > 0 ? "text-red-500" : "text-emerald-600"}`}>
                  <span className="text-sm font-bold block">
                    {overUnderNetTotal == null ? "—" : `${overUnderNetTotal > 0 ? "+" : ""}${fmt(overUnderNetTotal)}`}
                  </span>
                  {overUnderNetTotal != null && <span className="text-[10px] opacity-75">net over/under</span>}
                </div>
              </div>
            );
          })()}

          {/* Net Profit row — 100% minus every other category/payroll group's % of revenue.
              Unlike expense rows, exceeding the goal here is good, so the over/under coloring
              is inverted: at/above goal is green, below goal is red. */}
          {revBase != null && (() => {
            const netProfitAmount = revBase - totalCombined;
            const netProfitPct = 100 - (totalCombined / revBase) * 100;
            const npGoal = data.netProfitGoalPercentage;
            const npOverUnder = npGoal != null ? netProfitPct - npGoal : null;

            return (
              <div className={`${BD_COLS} px-6 py-3 bg-emerald-50 border-t border-emerald-100`}>
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Net Profit</span>
                <span />
                <span className={`text-sm font-bold text-right ${netProfitAmount < 0 ? "text-red-600" : "text-emerald-700"}`}>
                  {fmt(netProfitAmount)}
                </span>
                <span className={`text-sm font-bold text-right ${netProfitPct < 0 ? "text-red-600" : "text-emerald-700"}`}>
                  {fmtPct(netProfitPct)}
                </span>
                {editingNetProfitGoal ? (
                  <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      autoFocus
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={netProfitGoalInput}
                      onChange={(e) => setNetProfitGoalInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")  saveNetProfitGoal(netProfitGoalInput);
                        if (e.key === "Escape") setEditingNetProfitGoal(false);
                      }}
                      onBlur={() => saveNetProfitGoal(netProfitGoalInput)}
                      className="w-12 text-xs text-right bg-white border border-violet-400 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-violet-400"
                    />
                    <span className="text-xs text-gray-400">%</span>
                  </div>
                ) : (
                  <span
                    className={`text-sm text-right cursor-pointer hover:text-violet-600 transition-colors ${npGoal != null ? "text-emerald-700" : "text-emerald-300"}`}
                    title="Click to set goal %"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingNetProfitGoal(true);
                      setNetProfitGoalInput(npGoal != null ? String(npGoal) : "");
                    }}
                  >
                    {npGoal != null ? fmtPct(npGoal) : "—"}
                  </span>
                )}
                <div className={`text-right ${npOverUnder == null ? "text-emerald-300" : npOverUnder >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {npOverUnder == null ? (
                    <span className="text-sm font-medium">—</span>
                  ) : (() => {
                    const goalAmt = revBase * (npGoal! / 100);
                    const diffAmt = netProfitAmount - goalAmt;
                    return (
                      <>
                        <span className="text-sm font-medium block">{npOverUnder > 0 ? "+" : ""}{fmtPct(npOverUnder)}</span>
                        <span className="text-[10px] opacity-75">{diffAmt > 0 ? "+" : ""}{fmt(diffAmt)}</span>
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

function SingleMonthExpenseBreakdown({
  compareData,
  compareRevBase,
}: {
  compareData: BreakdownData | null;
  compareRevBase: number | null;
}) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // defaults to the current calendar month

  const [monthRev, setMonthRev] = useState<number | null>(null);
  const [revLoading, setRevLoading] = useState(true);

  useEffect(() => {
    setRevLoading(true);
    const mk = `${year}-${String(month).padStart(2, "0")}`;
    const todayStr = new Date().toISOString().split("T")[0];
    apiFetch(`/cash-flow/monthly-summary-batch?months=${mk}&serviceType=all&asOf=${todayStr}`)
      .then((batchData: { summaries: Record<string, { days?: DayData[] }> }) => {
        const days: DayData[] = batchData.summaries[mk]?.days || [];
        let total = 0;
        for (const day of days) {
          for (const r of day.revenue) total += r.amount;
          for (const a of (day.additionalIncome || [])) total += a.amount;
        }
        setMonthRev(total);
      })
      .catch(() => setMonthRev(null))
      .finally(() => setRevLoading(false));
  }, [year, month]);

  const monthLabel = new Date(year, month - 1).toLocaleString("en-US", { month: "long", year: "numeric" });

  const shiftMonth = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  return (
    <ExpenseBreakdownSection
      monthlyRev={monthRev}
      revenueLoading={revLoading}
      avgMonths={[{ year, month }]}
      avgLabel={monthLabel}
      title="Expense Breakdown"
      subtitlePrefix="Actual"
      compareData={compareData}
      compareRevBase={compareRevBase}
      headerExtra={
        <div className="flex items-center gap-1 ml-4">
          <button onClick={() => shiftMonth(-1)} className="p-1 rounded hover:bg-gray-100 text-gray-400" title="Previous month">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-[130px] text-center">{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} className="p-1 rounded hover:bg-gray-100 text-gray-400" title="Next month">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      }
    />
  );
}

// ─── Dashboard Tab ────────────────────────────────────────────────────────────

interface CostPerMinuteData {
  costPerMinute: number;
  totalMonthlyCosts: number;
  totalExpenses: number;
  totalPayroll: number;
  trailingMinutes: number;
  periodStart: string;
  periodEnd: string;
  projectionMonth: { year: number; month: number };
}

function DashboardTab() {
  const today = new Date();
  // Next 12 full months (exclude current month — it changes daily)
  const avg12Months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() + 1 + i, 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const avgLabel = `${new Date(avg12Months[0].year, avg12Months[0].month - 1).toLocaleString("en-US", { month: "short", year: "numeric" })} – ${new Date(avg12Months[11].year, avg12Months[11].month - 1).toLocaleString("en-US", { month: "short", year: "numeric" })}`;

  const [loading, setLoading]       = useState(true);
  const [monthlyRev, setMonthlyRev] = useState<number | null>(null);
  const [revBreakdown, setRevBreakdown] = useState<{ inbound: number; ai: number; outbound: number; other: number } | null>(null);
  const [cpmLoading, setCpmLoading] = useState(true);
  const [cpmData, setCpmData]       = useState<CostPerMinuteData | null>(null);
  const [avgBreakdownData, setAvgBreakdownData] = useState<BreakdownData | null>(null);
  const [avgBreakdownRevBase, setAvgBreakdownRevBase] = useState<number | null>(null);

  useEffect(() => {
    const todayStr = today.toISOString().split("T")[0];
    const monthsParam = avg12Months.map((m) => `${m.year}-${String(m.month).padStart(2, "0")}`).join(",");
    apiFetch(
      `/cash-flow/monthly-summary-batch?months=${monthsParam}&serviceType=all&asOf=${todayStr}`
    )
      .then((batchData: { summaries: Record<string, { days?: DayData[] }> }) => {
        const monthKeys = Object.keys(batchData.summaries);
        const n = monthKeys.length || 1;
        let inbound = 0, ai = 0, outbound = 0, other = 0, total = 0;
        for (const mk of monthKeys) {
          const days: DayData[] = batchData.summaries[mk]?.days || [];
          for (const day of days) {
            for (const r of day.revenue) {
              if (r.revenueType === "reactivation") outbound += r.amount;
              else if (r.revenueType === "initial_payment") other += r.amount;
              else if (r.serviceType === "ai_admin" || r.serviceType === "ai_medical") ai += r.amount;
              else inbound += r.amount;
              total += r.amount;
            }
            for (const a of (day.additionalIncome || [])) {
              other += a.amount;
              total += a.amount;
            }
          }
        }
        setMonthlyRev(total / n);
        setRevBreakdown({ inbound: inbound / n, ai: ai / n, outbound: outbound / n, other: other / n });
      })
      .catch(() => setMonthlyRev(null))
      .finally(() => setLoading(false));

    apiFetch("/cash-flow/cost-per-minute")
      .then((data: CostPerMinuteData) => setCpmData(data))
      .catch(() => setCpmData(null))
      .finally(() => setCpmLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const annualRev = monthlyRev !== null ? monthlyRev * 12 : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Revenue Overview</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Average of next 12 months ·{" "}
          <span className="font-medium text-gray-700">{avgLabel}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {/* Estimated Monthly Revenue */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-emerald-50">
              <CalendarDays className="h-5 w-5 text-emerald-600" />
            </div>
            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Est. Monthly Revenue
            </span>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : monthlyRev !== null ? (
            <p className="text-4xl font-bold text-gray-900 tracking-tight">
              {fmt(monthlyRev)}
            </p>
          ) : (
            <p className="text-sm text-gray-400">Unable to load</p>
          )}
          <p className="mt-2 text-xs text-gray-400">12-month avg · {avgLabel}</p>
        </div>

        {/* Estimated Annual Revenue */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-blue-50">
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Est. Annual Revenue
            </span>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : annualRev !== null ? (
            <p className="text-4xl font-bold text-gray-900 tracking-tight">
              {fmt(annualRev)}
            </p>
          ) : (
            <p className="text-sm text-gray-400">Unable to load</p>
          )}
          <p className="mt-2 text-xs text-gray-400">12-month avg × 12</p>
        </div>
      </div>

      {/* Revenue Breakdown Donut */}
      {revBreakdown && monthlyRev !== null && monthlyRev > 0 && (() => {
        const segments = [
          { label: "Inbound",       value: revBreakdown.inbound,  color: "#10b981" },
          { label: "AI",            value: revBreakdown.ai,       color: "#06b6d4" },
          { label: "Reactivations", value: revBreakdown.outbound, color: "#8b5cf6" },
          { label: "Other Income",  value: revBreakdown.other,    color: "#9ca3af" },
        ].filter((s) => s.value > 0);
        if (segments.length < 2) return null;
        const total = segments.reduce((s, x) => s + x.value, 0);
        return (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Revenue Breakdown</p>
            <div className="flex items-center gap-8">
              <div className="w-44 h-44 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={segments} dataKey="value" cx="50%" cy="50%" innerRadius="58%" outerRadius="80%" paddingAngle={2} strokeWidth={0}>
                      {segments.map((s) => <Cell key={s.label} fill={s.color} />)}
                    </Pie>
                    <ReTooltip
                      formatter={(value: number) => [fmt(value), ""]}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {segments.map((s) => (
                  <div key={s.label} className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex-shrink-0 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-sm text-gray-700 truncate">{s.label}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-gray-400">{((s.value / total) * 100).toFixed(1)}%</span>
                      <span className="text-sm font-semibold text-gray-900">{fmt(s.value)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Avg Cost Per Minute */}
      {(() => {
        const periodLabel = cpmData
          ? (() => {
              const fmt = (d: string) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
              return `${fmt(cpmData.periodStart)} – ${fmt(cpmData.periodEnd)}`;
            })()
          : null;
        const sixMonthLabel = cpmData
          ? new Date(cpmData.projectionMonth.year, cpmData.projectionMonth.month - 1, 1)
              .toLocaleString("en-US", { month: "long", year: "numeric" })
          : null;

        return (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-violet-50">
                <Activity className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Avg Cost Per Minute
                </span>
                {periodLabel && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Minutes: {periodLabel} (trailing 30 days)
                  </p>
                )}
              </div>
            </div>

            {cpmLoading ? (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Loading…</span>
              </div>
            ) : cpmData ? (
              <>
                <p className="text-4xl font-bold text-gray-900 tracking-tight">
                  ${cpmData.costPerMinute.toFixed(4)}
                  <span className="text-lg font-normal text-gray-400 ml-1">/ min</span>
                </p>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-0.5">Monthly Costs</p>
                    <p className="text-sm font-semibold text-gray-700">{fmt(cpmData.totalMonthlyCosts)}</p>
                    <p className="text-xs text-gray-400">{sixMonthLabel}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-0.5">Expenses</p>
                    <p className="text-sm font-semibold text-gray-700">{fmt(cpmData.totalExpenses)}</p>
                    <p className="text-xs text-gray-400">recurring</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 mb-0.5">Trailing Minutes</p>
                    <p className="text-sm font-semibold text-gray-700">
                      {cpmData.trailingMinutes.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-xs text-gray-400">last 30 days</p>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400">Unable to load</p>
            )}
          </div>
        );
      })()}

      <ExpenseBreakdownSection
        monthlyRev={monthlyRev}
        revenueLoading={loading}
        avgMonths={avg12Months}
        avgLabel={avgLabel}
        onDataLoaded={(data, revBase) => {
          setAvgBreakdownData(data);
          setAvgBreakdownRevBase(revBase);
        }}
      />

      <SingleMonthExpenseBreakdown
        compareData={avgBreakdownData}
        compareRevBase={avgBreakdownRevBase}
      />
    </div>
  );
}

const TAB_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  cashflow: "Cash Flow",
  expenses: "Expenses",
  "additional-income": "Additional Income",
  payroll: "Payroll & Contractor Pay",
  margin: "Margin",
  commissions: "Commissions",
};

export function CashFlow() {
  const [activeTab, setActiveTab] = useState<"dashboard" | "cashflow" | "expenses" | "additional-income" | "payroll" | "margin" | "commissions">("dashboard");

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-3">
          <TrendingUp className="h-6 w-6 text-emerald-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Daily Cash Flow</h1>
            <p className="text-sm text-gray-500">Revenue, expenses, and running balance</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-4 flex gap-1 border-b border-gray-200 -mb-px">
          {(["dashboard", "cashflow", "expenses", "additional-income", "payroll", "margin", "commissions"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-6">
        {activeTab === "dashboard" && <DashboardTab />}
        {activeTab === "cashflow" && <CashFlowGrid />}
        {activeTab === "expenses" && <ExpensesTab />}
        {activeTab === "additional-income" && <AdditionalIncomeTab />}
        {activeTab === "payroll" && <PayrollTab />}
        {activeTab === "margin" && <MarginTab />}
        {activeTab === "commissions" && <CommissionsTab />}
      </div>
    </div>
  );
}

// ─── Cash Flow Grid ───────────────────────────────────────────────────────────

type ServiceTypeFilter = "all" | "inbound" | "outbound";

const SERVICE_TYPE_OPTIONS: { value: ServiceTypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Reactivations" },
];

function CashFlowGrid() {
  const realTodayStr = new Date().toISOString().split("T")[0];
  const currentMonthKey = realTodayStr.substring(0, 7);

  const defaultBeginDate = useMemo(() => {
    const d = new Date(realTodayStr + "T00:00:00");
    d.setMonth(d.getMonth() - 2);
    return d.toISOString().split("T")[0];
  }, []);

  const [beginDate, setBeginDate] = useState(() => {
    return localStorage.getItem("cf_begin_date") || defaultBeginDate;
  });
  const [serviceType, setServiceType] = useState<ServiceTypeFilter>("all");
  const [startingBalance, setStartingBalance] = useState(0);
  const [editingBalance, setEditingBalance] = useState(false);
  const [balanceDraft, setBalanceDraft] = useState("");
  const [monthsData, setMonthsData] = useState<Map<string, DayData[]>>(new Map());
  const [loadingMonths, setLoadingMonths] = useState<Set<string>>(new Set());
  // Refs to avoid stale-closure races
  const inFlightRef       = useRef<Set<string>>(new Set());
  const loadedRef         = useRef<Set<string>>(new Set());
  const expandedMonthsRef = useRef<Set<string>>(new Set([currentMonthKey]));
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(
    () => new Set([currentMonthKey])
  );
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(
    () => new Set([getMondayKey(realTodayStr)])
  );
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const [dateOverrides, setDateOverrides] = useState<Record<string,string>>({});
  const [amountOverrides, setAmountOverrides] = useState<Record<string,number>>({});
  const [movingItem, setMovingItem] = useState<{
  key: string;
  type: "rev" | "exp" | "inc";
  targetDate : string;
} | null>(null);
  const [editingAmount, setEditingAmount] = useState<{ key: string; draft: string } | null>(null);

  // Load persisted starting balance for this user on mount
  useEffect(() => {
    apiFetch("/cash-flow/settings")
      .then((data: { startingBalance: number }) => setStartingBalance(data.startingBalance))
      .catch(() => {});
  }, []);

  // Load persisted exclusions for this user on mount
  useEffect(() => {
    apiFetch("/cash-flow/exclusions")
      .then((keys: string[]) => setExcluded(new Set(keys)))
      .catch(() => {});
  }, []);
  useEffect(() => {
    apiFetch("/cash-flow/date-overrides")
    .then((data: Record<string, string>) => setDateOverrides(data))
    .catch(() => {})
  }, [])
  useEffect(() => {
    apiFetch("/cash-flow/amount-overrides")
    .then((data: Record<string, number>) => setAmountOverrides(data))
    .catch(() => {})
  }, [])
  
  const toggleExclusion = useCallback(async (key: string) => {
    const isExcluded = excluded.has(key);
    // Optimistic update
    setExcluded((prev) => {
      const next = new Set(prev);
      if (isExcluded) next.delete(key); else next.add(key);
      return next;
    });
    try {
      if (isExcluded) {
        await apiFetch(`/cash-flow/exclusions/${encodeURIComponent(key)}`, { method: "DELETE" });
      } else {
        await apiFetch("/cash-flow/exclusions", {
          method: "POST",
          body: JSON.stringify({ key }),
        });
      }
    } catch {
      // Revert on failure
      setExcluded((prev) => {
        const next = new Set(prev);
        if (isExcluded) next.add(key); else next.delete(key);
        return next;
      });
      toast.error("Failed to update exclusion");
    }
  }, [excluded]);

  const applyDateOverride = useCallback(async (key: string, newDate: string) => {
    setDateOverrides((prev) => ({ ...prev, [key]: newDate }));
    setMovingItem(null);
    try {
      await apiFetch("/cash-flow/date-overrides", {
        method: "POST",
        body: JSON.stringify({ key, newDate }),
      });
    } catch {
      setDateOverrides((prev) => { const n = { ...prev }; delete n[key]; return n; });
      toast.error("Failed to save date override");
    }
  }, []);

  const removeDateOverride = useCallback(async (key: string, prevDate?: string) => {
    setDateOverrides((prev) => { const n = { ...prev }; delete n[key]; return n; });
    setMovingItem(null);
    try {
      await apiFetch(`/cash-flow/date-overrides/${encodeURIComponent(key)}`, { method: "DELETE" });
    } catch {
      if (prevDate) setDateOverrides((prev) => ({ ...prev, [key]: prevDate }));
      toast.error("Failed to remove date override");
    }
  }, []);

  // Dynamic range: from beginDate's month → 14 months forward
  const beginMonthKey = beginDate.substring(0, 7);
  const monthRange = useMemo(() => {
    const result: string[] = [];
    const base = new Date(beginDate + "T00:00:00");
    for (let i = 0; i <= 14; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
      result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return result;
  }, [beginDate]);

  // Load a batch of months in a single request — skips months already loaded
  // or in-flight. Replaces firing one request per month: the backend computes
  // every month's data from one shared set of DB queries instead of redoing
  // the same work once per month.
  const loadMonths = useCallback(async (monthKeys: string[]) => {
    const toFetch = monthKeys.filter((mk) => !loadedRef.current.has(mk) && !inFlightRef.current.has(mk));
    if (!toFetch.length) return;
    toFetch.forEach((mk) => inFlightRef.current.add(mk));
    setLoadingMonths((p) => new Set([...p, ...toFetch]));
    try {
      const data = await apiFetch(
        `/cash-flow/monthly-summary-batch?months=${toFetch.join(",")}&serviceType=${serviceType}&asOf=${realTodayStr}`
      );
      const summaries = (data.summaries || {}) as Record<string, { days?: DayData[] }>;
      setMonthsData((p) => {
        const next = new Map(p);
        for (const mk of toFetch) next.set(mk, (summaries[mk]?.days || []) as DayData[]);
        return next;
      });
      toFetch.forEach((mk) => loadedRef.current.add(mk));
    } catch {
      // months stay empty — user can click to retry
    } finally {
      toFetch.forEach((mk) => inFlightRef.current.delete(mk));
      setLoadingMonths((p) => { const s = new Set(p); toFetch.forEach((mk) => s.delete(mk)); return s; });
    }
  }, [serviceType]);

  // Single-month wrapper for on-demand expansion of one month outside the
  // auto-loaded range — same endpoint, just a 1-element batch.
  const loadMonth = useCallback((mk: string) => loadMonths([mk]), [loadMonths]);

  // Auto-load all months in the visible range (beginDate → 14 months forward) in one batch.
  // Future months load their projections so running balances populate without needing to expand.
  useEffect(() => {
    loadedRef.current   = new Set();
    inFlightRef.current = new Set();
    setMonthsData(new Map());
    setLoadingMonths(new Set());
    const autoLoad = monthRange.filter((mk) => mk >= beginMonthKey);
    const toLoad   = [...new Set([...autoLoad, ...expandedMonthsRef.current])];
    void loadMonths(toLoad);
  }, [loadMonths, beginMonthKey]); // loadMonths changes with serviceType/asOfDate; beginMonthKey changes with beginDate

  const refreshVisible = useCallback((expanded: Set<string>) => {
    loadedRef.current   = new Set();
    inFlightRef.current = new Set();
    setMonthsData(new Map());
    setLoadingMonths(new Set());
    const autoLoad = monthRange.filter((mk) => mk >= beginMonthKey);
    const toLoad   = [...new Set([...autoLoad, ...expanded])];
    void loadMonths(toLoad);
  }, [loadMonths, monthRange, beginMonthKey]);

  const moveExpenseSeries = useCallback(async (expenseId: string, newDate: string, key: string) => {
    const newDay = parseInt(newDate.split("-")[2]);
    setMovingItem(null);
    try {
      await apiFetch(`/cash-flow/expenses/${expenseId}`, {
        method: "PUT",
        body: JSON.stringify({ billDay: newDay }),
      });
      // Remove any single-occurrence override for this item (now redundant)
      const currentOverride = dateOverrides[key];
      if (currentOverride) {
        await apiFetch(`/cash-flow/date-overrides/${encodeURIComponent(key)}`, { method: "DELETE" });
        setDateOverrides((prev) => { const n = { ...prev }; delete n[key]; return n; });
      }
      refreshVisible(expandedMonths);
      toast.success("Expense schedule updated");
    } catch (e: any) {
      toast.error(e.message || "Failed to update expense schedule");
    }
  }, [dateOverrides, refreshVisible, expandedMonths]);

  const applyAmountOverride = useCallback(async (key: string, newAmount: number) => {
    setAmountOverrides((prev) => ({ ...prev, [key]: newAmount }));
    setEditingAmount(null);
    try {
      await apiFetch("/cash-flow/amount-overrides", {
        method: "POST",
        body: JSON.stringify({ key, newAmount }),
      });
    } catch {
      setAmountOverrides((prev) => { const n = { ...prev }; delete n[key]; return n; });
      toast.error("Failed to save amount override");
    }
  }, []);

  const removeAmountOverride = useCallback(async (key: string) => {
    const prev = amountOverrides[key];
    setAmountOverrides((p) => { const n = { ...p }; delete n[key]; return n; });
    setEditingAmount(null);
    try {
      await apiFetch(`/cash-flow/amount-overrides/${encodeURIComponent(key)}`, { method: "DELETE" });
    } catch {
      if (prev !== undefined) setAmountOverrides((p) => ({ ...p, [key]: prev }));
      toast.error("Failed to remove amount override");
    }
  }, [amountOverrides]);

  const changeExpenseSeriesAmount = useCallback(async (expenseId: string, newAmount: number, key: string) => {
    setEditingAmount(null);
    try {
      await apiFetch(`/cash-flow/expenses/${expenseId}`, {
        method: "PATCH",
        body: JSON.stringify({ amount: newAmount }),
      });
      // Remove single-occurrence override for this item if one exists
      if (amountOverrides[key] !== undefined) {
        await apiFetch(`/cash-flow/amount-overrides/${encodeURIComponent(key)}`, { method: "DELETE" });
        setAmountOverrides((p) => { const n = { ...p }; delete n[key]; return n; });
      }
      refreshVisible(expandedMonths);
      toast.success("Expense amount updated");
    } catch (e: any) {
      toast.error(e.message || "Failed to update expense amount");
    }
  }, [amountOverrides, refreshVisible, expandedMonths]);

  const processedMonthsData = useMemo(() => {
    if (Object.keys(dateOverrides).length === 0 && Object.keys(amountOverrides).length === 0) return monthsData;
    // Deep-copy all loaded days into a flat map
    const dayMap = new Map<string, DayData>();
    monthsData.forEach((days) => {
      days.forEach((d) => {
        dayMap.set(d.date, {
          ...d,
          revenue: [...d.revenue],
          expenses: [...d.expenses],
          additionalIncome: [...(d.additionalIncome || [])],
        });
      });
    });
    // Apply each override: remove from original date, insert at new date
    for (const [key, newDate] of Object.entries(dateOverrides)) {
      const parts = key.split(":");
      const [itemType, originalDate] = parts;
      const origDay = dayMap.get(originalDate);
      const targetDay = dayMap.get(newDate);
      if (!origDay) continue;
      if (itemType === "rev") {
        const idx = origDay.revenue.findIndex(
          (r) => revKey(originalDate, r.customerId, r.revenueType, r.revenueSubtype) === key
        );
        if (idx !== -1) {
          const [item] = origDay.revenue.splice(idx, 1);
          if (targetDay) targetDay.revenue.push(item);
        }
      } else if (itemType === "exp") {
        const idx = origDay.expenses.findIndex((e) => expKey(originalDate, e.id) === key);
        if (idx !== -1) {
          const [item] = origDay.expenses.splice(idx, 1);
          if (targetDay) targetDay.expenses.push(item);
        }
      } else if (itemType === "inc") {
        const incList = origDay.additionalIncome || [];
        const idx = incList.findIndex((a) => incKey(originalDate, a.id) === key);
        if (idx !== -1) {
          const [item] = incList.splice(idx, 1);
          if (targetDay) {
            if (!targetDay.additionalIncome) targetDay.additionalIncome = [];
            targetDay.additionalIncome.push(item);
          }
        }
      }
    }
    // Apply amount overrides: mutate amount in place on matched revenue/expense/income entries
    for (const [key, newAmount] of Object.entries(amountOverrides)) {
      const parts = key.split(":");
      const date = parts[1];
      const day = dayMap.get(date);
      if (!day) continue;
      if (parts[0] === "exp") {
        const exp = day.expenses.find((e) => expKey(date, e.id) === key);
        if (exp) exp.amount = newAmount;
      } else if (parts[0] === "inc") {
        const inc = (day.additionalIncome || []).find((a) => incKey(date, a.id) === key);
        if (inc) inc.amount = newAmount;
      } else if (parts[0] === "rev") {
        const rev = day.revenue.find((r) => revKey(date, r.customerId, r.revenueType, r.revenueSubtype) === key);
        if (rev) rev.amount = newAmount;
      }
    }
    // Rebuild month map
    const result = new Map<string, DayData[]>();
    monthsData.forEach((days, mk) => {
      result.set(mk, days.map((d) => dayMap.get(d.date) ?? d));
    });
    return result;
  }, [monthsData, dateOverrides, amountOverrides]);

  const saveBalance = async () => {
    const val = parseFloat(balanceDraft);
    if (!isNaN(val)) {
      setStartingBalance(val);
      try {
        await apiFetch("/cash-flow/settings", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ startingBalance: val }),
        });
      } catch {
        toast.error("Failed to save starting balance");
      }
    }
    setEditingBalance(false);
  };

  const toggleMonth = (key: string) => {
    const willExpand = !expandedMonths.has(key);
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (willExpand) next.add(key); else next.delete(key);
      expandedMonthsRef.current = next;
      return next;
    });
    // Call loadMonth OUTSIDE the updater so setLoadingMonths batches correctly
    // and the spinner renders immediately on the same render pass.
    if (willExpand) void loadMonth(key);
    setExpandedDays(new Set());
  };

  const toggleWeek = (key: string) => {
    setExpandedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setExpandedDays(new Set());
  };

  // For the first month, trim days that fall before beginDate
  const getDaysForMonth = useCallback((mk: string): DayData[] => {
    const days = processedMonthsData.get(mk) || [];
    if (mk === beginMonthKey) return days.filter((d) => d.date >= beginDate);
    return days;
  }, [processedMonthsData, beginDate, beginMonthKey]);

  // Carry opening balance forward month-by-month so each month starts where the previous closed
  const monthOpeningBalances = useMemo(() => {
    const map = new Map<string, number>();
    let running = startingBalance;
    for (const mk of monthRange) {
      if (mk < beginMonthKey) continue;
      map.set(mk, running);
      const mRows = computeRows(getDaysForMonth(mk), running, excluded);
      running += mRows.reduce((s, r) => s + r.dailyNet, 0);
    }
    return map;
  }, [startingBalance, monthRange, beginMonthKey, getDaysForMonth, excluded]);

  // KPI cards — current month only
  const currentOpeningBalance = monthOpeningBalances.get(currentMonthKey) ?? startingBalance;
  const currentRows    = computeRows(getDaysForMonth(currentMonthKey), currentOpeningBalance, excluded);
  const totalRevenue   = currentRows.reduce((s, r) => s + r.totalRevenue, 0);
  const totalExpenses  = currentRows.reduce((s, r) => s + r.totalExpenses, 0);
  const netFlow        = totalRevenue - totalExpenses;
  const closingBalance = currentOpeningBalance + netFlow;
  const curMonthName   = MONTH_NAMES[parseInt(currentMonthKey.substring(5, 7)) - 1];

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Begin date */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium">Begin Date:</span>
            <input
              type="date"
              value={beginDate}
              max={realTodayStr}
              onChange={(e) => { if (e.target.value) { setBeginDate(e.target.value); localStorage.setItem("cf_begin_date", e.target.value); setExpandedDays(new Set()); } }}
              className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            {beginDate !== defaultBeginDate && (
              <button
                onClick={() => { setBeginDate(defaultBeginDate); localStorage.setItem("cf_begin_date", defaultBeginDate); setExpandedDays(new Set()); }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Reset
              </button>
            )}
          </div>
          {/* Service type */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {SERVICE_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setServiceType(opt.value); setExpandedDays(new Set()); }}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                  serviceType === opt.value ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={() => refreshVisible(expandedMonths)} className="p-1.5 text-gray-400 hover:text-gray-700 transition" title="Refresh">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* KPI cards — current month */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500">Starting Balance</p>
          {editingBalance ? (
            <div className="flex items-center gap-1 mt-1">
              <input
                autoFocus
                className="border border-gray-200 rounded px-2 py-0.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-emerald-500"
                type="number"
                value={balanceDraft}
                onChange={(e) => setBalanceDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveBalance(); if (e.key === "Escape") setEditingBalance(false); }}
              />
              <button onClick={saveBalance} className="text-emerald-600 hover:text-emerald-800"><Check className="h-4 w-4" /></button>
            </div>
          ) : (
            <button className="text-xl font-bold text-gray-900 hover:text-emerald-700 transition text-left mt-1 block" onClick={() => { setBalanceDraft(startingBalance.toString()); setEditingBalance(true); }} title="Click to edit">
              {fmt(startingBalance)}
            </button>
          )}
          <p className="text-xs text-gray-400 mt-0.5">Click to edit</p>
        </div>
        <KPICard label={`Revenue In (${curMonthName})`} value={fmt(totalRevenue)} color="green" sub={`${currentRows.filter((r) => r.totalRevenue > 0).length} billing days`} />
        <KPICard label={`Expenses Out (${curMonthName})`} value={fmt(totalExpenses)} color="gray" sub={`${currentRows.filter((r) => r.totalExpenses > 0).length} expense days`} />
        <KPICard label="Closing Balance" value={fmt(closingBalance)} color={closingBalance >= 0 ? "green" : "red"} sub={`Net ${netFlow >= 0 ? "+" : ""}${fmt(netFlow)}`} />
      </div>

      {/* Multi-month hierarchical table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-48">Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Revenue In</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Expenses Out</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Net</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {monthRange.map((mk) => {
                const mkYear         = parseInt(mk.substring(0, 4));
                const mkMonth        = parseInt(mk.substring(5, 7));
                const isMonthExpanded = expandedMonths.has(mk);
                const isMonthLoading  = loadingMonths.has(mk);
                const isCurrent      = mk === currentMonthKey;
                const mOpeningBal    = monthOpeningBalances.get(mk) ?? startingBalance;
                const mRows          = computeRows(getDaysForMonth(mk), mOpeningBal, excluded);
                const mRevenue       = mRows.reduce((s, r) => s + r.totalRevenue, 0);
                const mExpenses      = mRows.reduce((s, r) => s + r.totalExpenses, 0);
                const mNet           = mRevenue - mExpenses;
                const mClose         = mOpeningBal + mNet;
                const mWeeks         = groupByWeeks(mRows);

                return (
                  <React.Fragment key={mk}>
                    {/* ── Month row ── */}
                    <tr
                      className={`cursor-pointer select-none border-t-2 border-gray-300 ${isCurrent ? "bg-blue-50 hover:bg-blue-100" : "bg-gray-200 hover:bg-gray-300"}`}
                      onClick={() => toggleMonth(mk)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {isMonthLoading
                            ? <Loader2 className="h-4 w-4 text-gray-400 animate-spin flex-shrink-0" />
                            : isMonthExpanded
                              ? <ChevronUp className="h-4 w-4 text-gray-600 flex-shrink-0" />
                              : <ChevronDown className="h-4 w-4 text-gray-600 flex-shrink-0" />
                          }
                          <span className="text-sm font-bold text-gray-800">{MONTH_NAMES[mkMonth - 1]} {mkYear}</span>
                          {isCurrent && <span className="text-xs text-blue-600 font-medium bg-blue-100 px-1.5 py-0.5 rounded">Current</span>}
                          {isMonthLoading && <span className="text-xs text-gray-400">Loading…</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold text-emerald-700">{mRevenue > 0 ? fmt(mRevenue) : <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold text-gray-700">{mExpenses > 0 ? fmt(mExpenses) : <span className="text-gray-400">—</span>}</td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold">
                        {mNet !== 0 ? <span className="text-gray-700">{mNet > 0 ? "+" : ""}{fmt(mNet)}</span> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold text-gray-800">{fmt(mClose)}</td>
                    </tr>

                    {/* ── Loading indicator when month expanded but data not yet ready ── */}
                    {isMonthExpanded && isMonthLoading && (
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-8 py-3 text-sm text-gray-400 text-center">Loading…</td>
                      </tr>
                    )}

                    {/* ── Weeks (when month expanded and loaded) ── */}
                    {isMonthExpanded && !isMonthLoading && mWeeks.map((week) => {
                      const isWeekExpanded = expandedWeeks.has(week.key);
                      return (
                        <React.Fragment key={week.key}>
                          {/* Week row */}
                          <tr className="bg-gray-100 hover:bg-gray-200 cursor-pointer select-none border-t border-gray-300" onClick={() => toggleWeek(week.key)}>
                            <td className="px-4 py-2 pl-8">
                              <div className="flex items-center gap-2">
                                {isWeekExpanded ? <ChevronUp className="h-4 w-4 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />}
                                <span className="text-sm font-semibold text-gray-700">{week.label}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right text-sm font-semibold text-emerald-700">{week.totalRevenue > 0 ? fmt(week.totalRevenue) : <span className="text-gray-300">—</span>}</td>
                            <td className="px-4 py-2 text-right text-sm font-semibold text-gray-700">{week.totalExpenses > 0 ? fmt(week.totalExpenses) : <span className="text-gray-300">—</span>}</td>
                            <td className="px-4 py-2 text-right text-sm font-medium">
                              {week.netFlow !== 0 ? <span className="text-gray-700">{week.netFlow > 0 ? "+" : ""}{fmt(week.netFlow)}</span> : <span className="text-gray-300">—</span>}
                            </td>
                            <td className={`px-4 py-2 text-right text-sm font-semibold ${week.closingBalance >= 0 ? "text-gray-700" : "text-red-600"}`}>{fmt(week.closingBalance)}</td>
                          </tr>

                          {/* Day rows (when week expanded) */}
                          {isWeekExpanded && week.days.map((row) => {
                            const isAsOf      = row.date === realTodayStr;
                            const isDayExpanded = expandedDays.has(row.date);
                            const hasActivity = row.revenue.length > 0 || row.expenses.length > 0 || (row.additionalIncome || []).length > 0;
                            const dd = parseInt(row.date.split("-")[2]);
                            const holidayName = getHolidayName(row.date);
                            return (
                              <React.Fragment key={row.date}>
                                <tr
                                  className={`transition ${isAsOf ? "bg-blue-50" : hasActivity ? "hover:bg-gray-50 cursor-pointer" : "opacity-60"}`}
                                  onClick={() => { if (!hasActivity) return; setExpandedDays(prev => { const s = new Set(prev); s.has(row.date) ? s.delete(row.date) : s.add(row.date); return s; }); }}
                                >
                                  <td className="px-4 py-2.5 pl-14">
                                    <div className="flex items-center gap-2">
                                      {isAsOf && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                                      <div>
                                        <span className="font-medium text-gray-900">{dd}</span>
                                        <span className="text-xs text-gray-400 ml-1.5">{DAY_NAMES[row.dayOfWeek]}</span>
                                        {isAsOf && <span className="ml-1.5 text-xs text-blue-500 font-medium">Today</span>}
                                        {holidayName && (
                                          <span className="ml-1.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                            {holidayName}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 text-right">{row.totalRevenue > 0 ? <span className="font-semibold text-emerald-700">{fmt(row.totalRevenue)}</span> : <span className="text-gray-200">—</span>}</td>
                                  <td className="px-4 py-2.5 text-right">{row.totalExpenses > 0 ? <span className="font-semibold text-gray-700">{fmt(row.totalExpenses)}</span> : <span className="text-gray-200">—</span>}</td>
                                  <td className="px-4 py-2.5 text-right">
                                    {row.dailyNet !== 0 ? <span className="font-medium text-gray-700">{row.dailyNet > 0 ? "+" : ""}{fmt(row.dailyNet)}</span> : <span className="text-gray-200">—</span>}
                                  </td>
                                  <td className="px-4 py-2.5 text-right"><span className={`font-semibold ${row.runningBalance >= 0 ? "text-gray-900" : "text-red-600"}`}>{fmt(row.runningBalance)}</span></td>
                                </tr>
                                {/* Day detail */}
                                {isDayExpanded && hasActivity && (
                                  <tr className="bg-gray-50 border-b border-gray-100">
                                    <td colSpan={5} className="px-8 py-4">
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {/* ── LEFT: inbound revenue + outbound revenue + additional income ── */}
                                        <div className="space-y-4">
                                          {(() => {
                                            const inbound = row.revenue.filter((r) => r.revenueType !== "reactivation" && r.revenueType !== "initial_payment");
                                            const outbound = row.revenue.filter((r) => r.revenueType === "reactivation");
                                            const initialPayments = row.revenue.filter((r) => r.revenueType === "initial_payment");
                                            const inboundTotal = inbound.reduce((s, r) => s + r.amount, 0);
                                            const outboundTotal = outbound.reduce((s, r) => s + r.amount, 0);
                                            const initialPaymentsTotal = initialPayments.reduce((s, r) => s + r.amount, 0);
                                            const additionalIncome = row.additionalIncome || [];
                                            const additionalTotal = additionalIncome.reduce((s, a) => s + a.amount, 0);
                                            return (
                                              <>
                                                {inbound.length > 0 && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-emerald-700 mb-2 uppercase tracking-wide">Inbound Revenue — {fmt(inboundTotal)}</p>
                                                    <div className="space-y-1.5">
                                                      {inbound.map((r) => {
                                                        const k = revKey(row.date, r.customerId, r.revenueType, r.revenueSubtype);
                                                        const isExcluded = excluded.has(k);
                                                        return (
                                                          <div key={k}>
                                                            <div className={`flex items-center justify-between text-xs ${isExcluded ? "opacity-40" : ""}`}>
                                                              <div className="flex items-center gap-1.5">
                                                                <button onClick={(e) => { e.stopPropagation(); void toggleExclusion(k); }} className={`flex-shrink-0 ${isExcluded ? "text-gray-400" : "text-emerald-500"}`} title={isExcluded ? "Click to include" : "Click to exclude"}>
                                                                  {isExcluded ? <Circle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                                                </button>
                                                                <span className={`text-gray-700 ${isExcluded ? "line-through" : ""}`}>{r.customerName}</span>
                                                                {(r.serviceType === "ai_admin" || r.serviceType === "ai_medical") && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700">AI</span>
                                                                )}
                                                                {r.revenueSubtype === "minimum" && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-600">Min</span>
                                                                )}
                                                                {r.revenueSubtype === "overage" && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-600">Overage</span>
                                                                )}
                                                                {r.revenueSubtype === "weekly_overage" && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-purple-100 text-purple-600">Weekly Overage</span>
                                                                )}
                                                                {r.revenueSubtype === "weekly_overage_catchup" && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-pink-100 text-pink-600">Overage Catch-up</span>
                                                                )}
                                                                {r.periodStart && r.periodEnd && (
                                                                  <span className="text-[10px] text-gray-400" title="Usage period this charge covers">
                                                                    {new Date(r.periodStart + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                                                    {" – "}
                                                                    {new Date(r.periodEnd + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                                                  </span>
                                                                )}
                                                              </div>
                                                              <div className="flex items-center gap-2">
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.type === "actual" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-600"}`}>{r.type === "actual" ? "Actual" : "Projected"}</span>
                                                                <span className="font-semibold text-emerald-700">{fmt(r.amount)}</span>
                                                                <button onClick={(e) => { e.stopPropagation(); setEditingAmount(editingAmount?.key === k ? null : { key: k, draft: String(amountOverrides[k] ?? r.amount) }); setMovingItem(null); }} className={`flex-shrink-0 transition ${amountOverrides[k] !== undefined ? "text-emerald-500" : "text-gray-300 hover:text-gray-500"}`} title={amountOverrides[k] !== undefined ? `Amount overridden to ${fmt(amountOverrides[k])} — click to change` : "Override amount"}>
                                                                  <DollarSign className="h-3.5 w-3.5" />
                                                                </button>
                                                                <button onClick={(e) => { e.stopPropagation(); setMovingItem(movingItem?.key === k ? null : { key: k, type: "rev", targetDate: dateOverrides[k] || nextDate(row.date) }); setEditingAmount(null); }} className={`flex-shrink-0 transition ${dateOverrides[k] ? "text-blue-500" : "text-gray-300 hover:text-gray-500"}`} title={dateOverrides[k] ? `Moved to ${dateOverrides[k]} — click to change` : "Move to different date"}>
                                                                  <Calendar className="h-3.5 w-3.5" />
                                                                </button>
                                                              </div>
                                                            </div>
                                                            {editingAmount?.key === k && (
                                                              <div className="mt-1 ml-5 flex items-center gap-2 flex-wrap text-xs" onClick={(e) => e.stopPropagation()}>
                                                                <input type="number" min="0" step="0.01" value={editingAmount.draft} onChange={(e) => setEditingAmount((p) => p ? { ...p, draft: e.target.value } : p)} className="border border-gray-200 rounded px-2 py-0.5 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                                                                <button onClick={() => { const v = parseFloat(editingAmount.draft); if (!isNaN(v) && v !== r.amount) void applyAmountOverride(k, v); else setEditingAmount(null); }} disabled={!editingAmount.draft || isNaN(parseFloat(editingAmount.draft))} className="px-2 py-0.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 transition">Just this one</button>
                                                                {amountOverrides[k] !== undefined && <button onClick={() => void removeAmountOverride(k)} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition">Remove override</button>}
                                                                <button onClick={() => setEditingAmount(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                                                              </div>
                                                            )}
                                                            {movingItem?.key === k && (
                                                              <div className="mt-1 ml-5 flex items-center gap-2 flex-wrap text-xs" onClick={(e) => e.stopPropagation()}>
                                                                <input type="date" value={movingItem.targetDate} onChange={(e) => setMovingItem((p) => p ? { ...p, targetDate: e.target.value } : p)} className="border border-gray-200 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                                                <button onClick={() => { if (movingItem.targetDate && movingItem.targetDate !== row.date) void applyDateOverride(k, movingItem.targetDate); }} disabled={!movingItem.targetDate || movingItem.targetDate === row.date} className="px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 transition">Move</button>
                                                                {dateOverrides[k] && <button onClick={() => void removeDateOverride(k, dateOverrides[k])} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition">Remove override</button>}
                                                                <button onClick={() => setMovingItem(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                )}
                                                {outbound.length > 0 && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-purple-700 mb-2 uppercase tracking-wide">Outbound Revenue — {fmt(outboundTotal)}</p>
                                                    <div className="space-y-1.5">
                                                      {outbound.map((r) => {
                                                        const k = revKey(row.date, r.customerId, r.revenueType, r.revenueSubtype);
                                                        const isExcluded = excluded.has(k);
                                                        return (
                                                          <div key={k}>
                                                            <div className={`flex items-center justify-between text-xs ${isExcluded ? "opacity-40" : ""}`}>
                                                              <div className="flex items-center gap-1.5">
                                                                <button onClick={(e) => { e.stopPropagation(); void toggleExclusion(k); }} className={`flex-shrink-0 ${isExcluded ? "text-gray-400" : "text-purple-500"}`} title={isExcluded ? "Click to include" : "Click to exclude"}>
                                                                  {isExcluded ? <Circle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                                                </button>
                                                                <span className={`text-gray-700 ${isExcluded ? "line-through" : ""}`}>{r.customerName}</span>
                                                                {(r.serviceType === "ai_admin" || r.serviceType === "ai_medical") && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700">AI</span>
                                                                )}
                                                                {r.revenueSubtype === "minimum" && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-600">Min</span>
                                                                )}
                                                                {r.revenueSubtype === "overage" && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-600">Overage</span>
                                                                )}
                                                                {r.revenueSubtype === "weekly_overage" && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-purple-100 text-purple-600">Weekly Overage</span>
                                                                )}
                                                                {r.revenueSubtype === "weekly_overage_catchup" && (
                                                                  <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-pink-100 text-pink-600">Overage Catch-up</span>
                                                                )}
                                                                {r.periodStart && r.periodEnd && (
                                                                  <span className="text-[10px] text-gray-400" title="Usage period this charge covers">
                                                                    {new Date(r.periodStart + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                                                    {" – "}
                                                                    {new Date(r.periodEnd + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                                                  </span>
                                                                )}
                                                              </div>
                                                              <div className="flex items-center gap-2">
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.type === "actual" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-600"}`}>{r.type === "actual" ? "Actual" : "Projected"}</span>
                                                                <span className="font-semibold text-purple-700">{fmt(r.amount)}</span>
                                                                <button onClick={(e) => { e.stopPropagation(); setEditingAmount(editingAmount?.key === k ? null : { key: k, draft: String(amountOverrides[k] ?? r.amount) }); setMovingItem(null); }} className={`flex-shrink-0 transition ${amountOverrides[k] !== undefined ? "text-emerald-500" : "text-gray-300 hover:text-gray-500"}`} title={amountOverrides[k] !== undefined ? `Amount overridden to ${fmt(amountOverrides[k])} — click to change` : "Override amount"}>
                                                                  <DollarSign className="h-3.5 w-3.5" />
                                                                </button>
                                                                <button onClick={(e) => { e.stopPropagation(); setMovingItem(movingItem?.key === k ? null : { key: k, type: "rev", targetDate: dateOverrides[k] || nextDate(row.date) }); setEditingAmount(null); }} className={`flex-shrink-0 transition ${dateOverrides[k] ? "text-blue-500" : "text-gray-300 hover:text-gray-500"}`} title={dateOverrides[k] ? `Moved to ${dateOverrides[k]} — click to change` : "Move to different date"}>
                                                                  <Calendar className="h-3.5 w-3.5" />
                                                                </button>
                                                              </div>
                                                            </div>
                                                            {editingAmount?.key === k && (
                                                              <div className="mt-1 ml-5 flex items-center gap-2 flex-wrap text-xs" onClick={(e) => e.stopPropagation()}>
                                                                <input type="number" min="0" step="0.01" value={editingAmount.draft} onChange={(e) => setEditingAmount((p) => p ? { ...p, draft: e.target.value } : p)} className="border border-gray-200 rounded px-2 py-0.5 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                                                                <button onClick={() => { const v = parseFloat(editingAmount.draft); if (!isNaN(v) && v !== r.amount) void applyAmountOverride(k, v); else setEditingAmount(null); }} disabled={!editingAmount.draft || isNaN(parseFloat(editingAmount.draft))} className="px-2 py-0.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 transition">Just this one</button>
                                                                {amountOverrides[k] !== undefined && <button onClick={() => void removeAmountOverride(k)} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition">Remove override</button>}
                                                                <button onClick={() => setEditingAmount(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                                                              </div>
                                                            )}
                                                            {movingItem?.key === k && (
                                                              <div className="mt-1 ml-5 flex items-center gap-2 flex-wrap text-xs" onClick={(e) => e.stopPropagation()}>
                                                                <input type="date" value={movingItem.targetDate} onChange={(e) => setMovingItem((p) => p ? { ...p, targetDate: e.target.value } : p)} className="border border-gray-200 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                                                <button onClick={() => { if (movingItem.targetDate && movingItem.targetDate !== row.date) void applyDateOverride(k, movingItem.targetDate); }} disabled={!movingItem.targetDate || movingItem.targetDate === row.date} className="px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 transition">Move</button>
                                                                {dateOverrides[k] && <button onClick={() => void removeDateOverride(k, dateOverrides[k])} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition">Remove override</button>}
                                                                <button onClick={() => setMovingItem(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                )}
                                                {initialPayments.length > 0 && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-amber-700 mb-2 uppercase tracking-wide">Initial Payments — {fmt(initialPaymentsTotal)}</p>
                                                    <div className="space-y-1.5">
                                                      {initialPayments.map((r) => {
                                                        const k = revKey(row.date, r.customerId, r.revenueType, r.revenueSubtype);
                                                        const isExcluded = excluded.has(k);
                                                        return (
                                                          <div key={k}>
                                                            <div className={`flex items-center justify-between text-xs ${isExcluded ? "opacity-40" : ""}`}>
                                                              <div className="flex items-center gap-1.5">
                                                                <button onClick={(e) => { e.stopPropagation(); void toggleExclusion(k); }} className={`flex-shrink-0 ${isExcluded ? "text-gray-400" : "text-amber-500"}`} title={isExcluded ? "Click to include" : "Click to exclude"}>
                                                                  {isExcluded ? <Circle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                                                </button>
                                                                <span className={`text-gray-700 ${isExcluded ? "line-through" : ""}`}>{r.customerName}</span>
                                                                <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700">Onboarding</span>
                                                              </div>
                                                              <div className="flex items-center gap-2">
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${r.type === "actual" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-600"}`}>{r.type === "actual" ? "Actual" : "Projected"}</span>
                                                                <span className="font-semibold text-amber-700">{fmt(r.amount)}</span>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                )}
                                                {additionalIncome.length > 0 && (
                                                  <div>
                                                    <p className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">Additional Income — {fmt(additionalTotal)}</p>
                                                    <div className="space-y-1.5">
                                                      {additionalIncome.map((a) => {
                                                        const k = incKey(row.date, a.id);
                                                        const isExcluded = excluded.has(k);
                                                        return (
                                                          <div key={a.id}>
                                                            <div className={`flex items-center justify-between text-xs ${isExcluded ? "opacity-40" : ""}`}>
                                                              <div className="flex items-center gap-1.5">
                                                                <button onClick={(e) => { e.stopPropagation(); void toggleExclusion(k); }} className={`flex-shrink-0 ${isExcluded ? "text-gray-400" : "text-blue-500"}`} title={isExcluded ? "Click to include" : "Click to exclude"}>
                                                                  {isExcluded ? <Circle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                                                </button>
                                                                <span className={`text-gray-700 ${isExcluded ? "line-through" : ""}`}>{a.label}</span>
                                                                {a.notes && <span className="text-gray-400">· {a.notes}</span>}
                                                              </div>
                                                              <div className="flex items-center gap-2">
                                                                <span className="font-semibold text-blue-700">{fmt(a.amount)}</span>
                                                                <button onClick={(e) => { e.stopPropagation(); setEditingAmount(editingAmount?.key === k ? null : { key: k, draft: String(amountOverrides[k] ?? a.amount) }); setMovingItem(null); }} className={`flex-shrink-0 transition ${amountOverrides[k] !== undefined ? "text-emerald-500" : "text-gray-300 hover:text-gray-500"}`} title={amountOverrides[k] !== undefined ? `Amount overridden to ${fmt(amountOverrides[k])} — click to change` : "Override amount"}>
                                                                  <DollarSign className="h-3.5 w-3.5" />
                                                                </button>
                                                                <button onClick={(e) => { e.stopPropagation(); setMovingItem(movingItem?.key === k ? null : { key: k, type: "inc", targetDate: dateOverrides[k] || nextDate(row.date) }); setEditingAmount(null); }} className={`flex-shrink-0 transition ${dateOverrides[k] ? "text-blue-500" : "text-gray-300 hover:text-gray-500"}`} title={dateOverrides[k] ? `Moved to ${dateOverrides[k]} — click to change` : "Move to different date"}>
                                                                  <Calendar className="h-3.5 w-3.5" />
                                                                </button>
                                                              </div>
                                                            </div>
                                                            {editingAmount?.key === k && (
                                                              <div className="mt-1 ml-5 flex items-center gap-2 flex-wrap text-xs" onClick={(e) => e.stopPropagation()}>
                                                                <input type="number" min="0" step="0.01" value={editingAmount.draft} onChange={(e) => setEditingAmount((p) => p ? { ...p, draft: e.target.value } : p)} className="border border-gray-200 rounded px-2 py-0.5 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                                                                <button onClick={() => { const v = parseFloat(editingAmount.draft); if (!isNaN(v) && v !== a.amount) void applyAmountOverride(k, v); else setEditingAmount(null); }} disabled={!editingAmount.draft || isNaN(parseFloat(editingAmount.draft))} className="px-2 py-0.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 transition">Just this one</button>
                                                                {amountOverrides[k] !== undefined && <button onClick={() => void removeAmountOverride(k)} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition">Remove override</button>}
                                                                <button onClick={() => setEditingAmount(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                                                              </div>
                                                            )}
                                                            {movingItem?.key === k && (
                                                              <div className="mt-1 ml-5 flex items-center gap-2 flex-wrap text-xs" onClick={(e) => e.stopPropagation()}>
                                                                <input type="date" value={movingItem.targetDate} onChange={(e) => setMovingItem((p) => p ? { ...p, targetDate: e.target.value } : p)} className="border border-gray-200 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                                                <button onClick={() => { if (movingItem.targetDate && movingItem.targetDate !== row.date) void applyDateOverride(k, movingItem.targetDate); }} disabled={!movingItem.targetDate || movingItem.targetDate === row.date} className="px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 transition">Move</button>
                                                                {dateOverrides[k] && <button onClick={() => void removeDateOverride(k, dateOverrides[k])} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition">Remove override</button>}
                                                                <button onClick={() => setMovingItem(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                                                              </div>
                                                            )}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                )}
                                              </>
                                            );
                                          })()}
                                        </div>

                                        {/* ── RIGHT: expenses ── */}
                                        <div>
                                          {row.expenses.length > 0 && (
                                            <div>
                                              <p className="text-xs font-semibold text-red-600 mb-2 uppercase tracking-wide">Expenses — {fmt(row.totalExpenses)}</p>
                                              <div className="space-y-1.5">
                                                {row.expenses.map((e) => {
                                                  const k = expKey(row.date, e.id);
                                                  const isExcluded = excluded.has(k);
                                                  return (
                                                    <div key={e.id}>
                                                      <div className={`flex items-center justify-between text-xs ${isExcluded ? "opacity-40" : ""}`}>
                                                        <div className="flex items-center gap-1.5">
                                                          <button onClick={(ev) => { ev.stopPropagation(); void toggleExclusion(k); }} className={`flex-shrink-0 ${isExcluded ? "text-gray-400" : "text-red-400"}`} title={isExcluded ? "Click to include" : "Click to exclude"}>
                                                            {isExcluded ? <Circle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                                                          </button>
                                                          {e.categoryColor && <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: e.categoryColor }} />}
                                                          <span className={`text-gray-700 ${isExcluded ? "line-through" : ""}`}>{e.name}</span>
                                                          {e.source === "payroll" && (
                                                            <span className="px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-600">Payroll</span>
                                                          )}
                                                          {e.categoryName && e.source !== "payroll" && <span className="text-gray-400">· {e.categoryName}</span>}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                          <span className="font-semibold text-red-600">{fmt(e.amount)}</span>
                                                          <button onClick={(ev) => { ev.stopPropagation(); setEditingAmount(editingAmount?.key === k ? null : { key: k, draft: String(amountOverrides[k] ?? e.amount) }); setMovingItem(null); }} className={`flex-shrink-0 transition ${amountOverrides[k] !== undefined ? "text-emerald-500" : "text-gray-300 hover:text-gray-500"}`} title={amountOverrides[k] !== undefined ? `Amount overridden to ${fmt(amountOverrides[k])} — click to change` : "Override amount"}>
                                                            <DollarSign className="h-3.5 w-3.5" />
                                                          </button>
                                                          <button onClick={(ev) => { ev.stopPropagation(); setMovingItem(movingItem?.key === k ? null : { key: k, type: "exp", targetDate: dateOverrides[k] || nextDate(row.date) }); setEditingAmount(null); }} className={`flex-shrink-0 transition ${dateOverrides[k] ? "text-blue-500" : "text-gray-300 hover:text-gray-500"}`} title={dateOverrides[k] ? `Moved to ${dateOverrides[k]} — click to change` : "Move to different date"}>
                                                            <Calendar className="h-3.5 w-3.5" />
                                                          </button>
                                                        </div>
                                                      </div>
                                                      {editingAmount?.key === k && (
                                                        <div className="mt-1 ml-5 flex items-center gap-2 flex-wrap text-xs" onClick={(ev) => ev.stopPropagation()}>
                                                          <input type="number" min="0" step="0.01" value={editingAmount.draft} onChange={(ev) => setEditingAmount((p) => p ? { ...p, draft: ev.target.value } : p)} className="border border-gray-200 rounded px-2 py-0.5 text-xs w-28 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                                                          <button onClick={() => { const v = parseFloat(editingAmount.draft); if (!isNaN(v) && v !== e.amount) void applyAmountOverride(k, v); else setEditingAmount(null); }} disabled={!editingAmount.draft || isNaN(parseFloat(editingAmount.draft))} className="px-2 py-0.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 transition">Just this one</button>
                                                          {e.source !== "payroll" && (
                                                            <button onClick={() => { const v = parseFloat(editingAmount.draft); if (!isNaN(v)) void changeExpenseSeriesAmount(e.id, v, k); }} disabled={!editingAmount.draft || isNaN(parseFloat(editingAmount.draft))} className="px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 transition">Entire series</button>
                                                          )}
                                                          {amountOverrides[k] !== undefined && <button onClick={() => void removeAmountOverride(k)} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition">Remove override</button>}
                                                          <button onClick={() => setEditingAmount(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                                                        </div>
                                                      )}
                                                      {movingItem?.key === k && (
                                                        <div className="mt-1 ml-5 flex items-center gap-2 flex-wrap text-xs" onClick={(ev) => ev.stopPropagation()}>
                                                          <input type="date" value={movingItem.targetDate} onChange={(ev) => setMovingItem((p) => p ? { ...p, targetDate: ev.target.value } : p)} className="border border-gray-200 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                                                          <button onClick={() => { if (movingItem.targetDate && movingItem.targetDate !== row.date) void applyDateOverride(k, movingItem.targetDate); }} disabled={!movingItem.targetDate || movingItem.targetDate === row.date} className="px-2 py-0.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 transition">Just this one</button>
                                                          {e.source !== "payroll" && (
                                                            <button onClick={() => { if (movingItem.targetDate && movingItem.targetDate !== row.date) void moveExpenseSeries(e.id, movingItem.targetDate, k); }} disabled={!movingItem.targetDate || movingItem.targetDate === row.date} className="px-2 py-0.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 transition">Move series</button>
                                                          )}
                                                          {dateOverrides[k] && <button onClick={() => void removeDateOverride(k, dateOverrides[k])} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition">Remove override</button>}
                                                          <button onClick={() => setMovingItem(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-emerald-100 border border-emerald-300" />Inbound revenue (actual)</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-purple-100 border border-purple-300" />Outbound revenue</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-blue-100 border border-blue-300" />Projected / Additional income</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-blue-50 border border-blue-200" />As of date</span>
        <span>Click a month · Click a week · Click a day to expand</span>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KPICard({
  label,
  value,
  color,
  sub,
}: {
  label: string;
  value: string;
  color: string;
  sub?: string;
}) {
  const textColors: Record<string, string> = {
    green: "text-emerald-700",
    red: "text-red-600",
    blue: "text-blue-600",
  };
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`text-xl font-bold mt-1 ${textColors[color] || "text-gray-900"}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Additional Income Tab ────────────────────────────────────────────────────

interface AdditionalIncomeEntry {
  id: string;
  label: string;
  amount: number;
  frequency: Expense["frequency"];
  billDay?: number;
  billMonth?: number;
  startDate?: string;
  endDate?: string;
  notes?: string;
  isActive: boolean;
}

const blankIncome = (): Partial<AdditionalIncomeEntry> => ({
  label: "",
  amount: 0,
  frequency: "one-time",
  billDay: undefined,
  billMonth: undefined,
  startDate: new Date().toISOString().split("T")[0],
  endDate: undefined,
  notes: "",
  isActive: true,
});

function incomeScheduleLabel(item: AdditionalIncomeEntry): string {
  switch (item.frequency) {
    case "daily":    return "Every day";
    case "one-time": return item.startDate ? `On ${item.startDate}` : "One-time";
    case "weekly":   return item.billDay != null ? `Every ${DAY_OF_WEEK[item.billDay]}` : "Weekly";
    case "monthly":  return item.billDay ? `${ordinal(item.billDay)} of each month` : "Monthly";
    case "quarterly":
      return item.billDay && item.billMonth
        ? `${ordinal(item.billDay)} of ${MONTHS[(item.billMonth - 1) % 12]} (quarterly)`
        : "Quarterly";
    case "yearly":
      return item.billDay && item.billMonth
        ? `${MONTHS[item.billMonth - 1]} ${ordinal(item.billDay)}`
        : "Yearly";
    default: return item.frequency;
  }
}

function AdditionalIncomeTab() {
  const [items, setItems] = useState<AdditionalIncomeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; editing?: AdditionalIncomeEntry }>({ open: false });
  const [deleting, setDeleting] = useState<AdditionalIncomeEntry | null>(null);
  const [form, setForm] = useState<Partial<AdditionalIncomeEntry>>(blankIncome());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/cash-flow/additional-income");
      setItems(data);
    } catch (e: any) {
      toast.error(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(blankIncome()); setModal({ open: true }); };
  const openEdit = (item: AdditionalIncomeEntry) => { setForm({ ...item }); setModal({ open: true, editing: item }); };

  const save = async () => {
    if (!form.label?.trim()) return toast.error("Label is required");
    if (!form.amount || form.amount <= 0) return toast.error("Amount must be greater than 0");
    if (!form.frequency) return toast.error("Frequency is required");
    setSaving(true);
    try {
      const body: Record<string, any> = {
        label: form.label, amount: form.amount, frequency: form.frequency,
        billDay: form.billDay ?? null, billMonth: form.billMonth ?? null,
        startDate: form.startDate ?? null, endDate: form.endDate ?? null,
        notes: form.notes || null,
      };
      if (modal.editing) {
        body.isActive = form.isActive;
        await apiFetch(`/cash-flow/additional-income/${modal.editing.id}`, { method: "PUT", body: JSON.stringify(body) });
        toast.success("Updated");
      } else {
        await apiFetch("/cash-flow/additional-income", { method: "POST", body: JSON.stringify(body) });
        toast.success("Added");
      }
      setModal({ open: false });
      load();
    } catch (e: any) {
      toast.error(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await apiFetch(`/cash-flow/additional-income/${deleting.id}`, { method: "DELETE" });
      toast.success("Deleted");
      setDeleting(null);
      load();
    } catch (e: any) {
      toast.error(e.message || "Failed to delete");
    }
  };

  const visible = items.filter((i) => showInactive || i.isActive);
  const freq = form.frequency;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-gray-900">Additional Income</h2>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded" />
            Show inactive
          </label>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition"
        >
          <Plus className="h-4 w-4" /> Add Income
        </button>
      </div>

      {/* Summary card */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-purple-200 px-4 py-3">
          <p className="text-xs font-medium text-gray-500">Active Income Sources</p>
          <p className="text-xl font-bold text-purple-700 mt-1">{items.filter((i) => i.isActive).length}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {items.filter((i) => i.isActive && i.frequency === "monthly").length} monthly,{" "}
            {items.filter((i) => i.isActive && i.frequency === "one-time").length} one-time
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            No income entries yet.{" "}
            <button onClick={openAdd} className="text-purple-600 hover:underline">Add one</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Label</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Schedule</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Notes</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visible.map((item) => (
                <tr key={item.id} className={`hover:bg-gray-50 transition ${!item.isActive ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {item.label}
                    {!item.isActive && <span className="ml-2 text-xs text-gray-400 font-normal">(inactive)</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    <span className="capitalize">{FREQUENCY_LABELS[item.frequency]}</span>
                    <span className="text-gray-400 text-xs ml-1.5">· {incomeScheduleLabel(item)}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{item.notes || "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold text-purple-700">{fmt(Number(item.amount))}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(item)} className="text-gray-400 hover:text-blue-600 transition" title="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleting(item)} className="text-gray-400 hover:text-red-600 transition" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit modal */}
      {modal.open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">{modal.editing ? "Edit" : "Add"} Income</h3>
              <button onClick={() => setModal({ open: false })} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label <span className="text-red-500">*</span></label>
                <input className="input w-full" placeholder="e.g. Partner contribution, Grant, Referral fee…"
                  value={form.label || ""} onChange={(e) => setForm({ ...form, label: e.target.value })} />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount <span className="text-red-500">*</span></label>
                <input className="input w-full" type="number" min="0.01" step="0.01" placeholder="0.00"
                  value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} />
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frequency <span className="text-red-500">*</span></label>
                <select className="input w-full" value={form.frequency || "one-time"}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value as Expense["frequency"], billDay: undefined, billMonth: undefined, startDate: undefined })}>
                  {(["one-time","monthly","weekly","yearly","quarterly","daily"] as Expense["frequency"][]).map((f) => (
                    <option key={f} value={f}>{FREQUENCY_LABELS[f]}</option>
                  ))}
                </select>
              </div>

              {/* Frequency-specific fields — mirrors ExpensesTab */}
              {freq === "one-time" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                  <input className="input w-full" type="date" value={form.startDate || ""}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                </div>
              )}
              {freq === "monthly" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month <span className="text-red-500">*</span></label>
                  <input className="input w-full" type="number" min={1} max={31} placeholder="e.g. 1"
                    value={form.billDay ?? ""} onChange={(e) => setForm({ ...form, billDay: parseInt(e.target.value) || undefined })} />
                </div>
              )}
              {freq === "weekly" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week <span className="text-red-500">*</span></label>
                  <select className="input w-full" value={form.billDay ?? ""}
                    onChange={(e) => setForm({ ...form, billDay: parseInt(e.target.value) })}>
                    <option value="">Select…</option>
                    {DAY_OF_WEEK.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
              )}
              {(freq === "yearly" || freq === "quarterly") && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month <span className="text-red-500">*</span></label>
                    <select className="input w-full" value={form.billMonth ?? ""}
                      onChange={(e) => setForm({ ...form, billMonth: parseInt(e.target.value) || undefined })}>
                      <option value="">Select…</option>
                      {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Day <span className="text-red-500">*</span></label>
                    <input className="input w-full" type="number" min={1} max={31} placeholder="e.g. 1"
                      value={form.billDay ?? ""} onChange={(e) => setForm({ ...form, billDay: parseInt(e.target.value) || undefined })} />
                  </div>
                </div>
              )}

              {/* Start / End date (for recurring) */}
              {freq !== "one-time" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input className="input w-full" type="date" value={form.startDate || ""}
                      onChange={(e) => setForm({ ...form, startDate: e.target.value || undefined })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <div className="flex items-center gap-1">
                      <input className="input w-full flex-1" type="date" value={form.endDate || ""}
                        onChange={(e) => setForm({ ...form, endDate: e.target.value || undefined })} />
                      {form.endDate && (
                        <button type="button" onClick={() => setForm({ ...form, endDate: undefined })} className="text-gray-400 hover:text-gray-600 px-1" title="Clear end date">✕</button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea className="input w-full resize-none" rows={2} placeholder="Optional details…"
                  value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>

              {/* Active toggle (edit only) */}
              {modal.editing && (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.isActive ?? true}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="rounded" />
                  Active
                </label>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setModal({ open: false })} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition">Cancel</button>
              <button onClick={save} disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition disabled:opacity-50">
                {saving ? "Saving…" : modal.editing ? "Save Changes" : "Add Income"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleting && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm px-6 py-5 space-y-4">
            <h3 className="font-semibold text-gray-900">Delete Income Entry</h3>
            <p className="text-sm text-gray-600">
              Remove <strong>{deleting.label}</strong> ({fmt(Number(deleting.amount))} · {FREQUENCY_LABELS[deleting.frequency]})? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Expenses Tab ─────────────────────────────────────────────────────────────

function ExpensesTab() {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showPastOneTime, setShowPastOneTime] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Upcoming occurrences state
  const [expandedExpenses, setExpandedExpenses] = useState<Set<string>>(new Set());
  const [upcomingData, setUpcomingData] = useState<Record<string, UpcomingOccurrence[]>>({});
  const [upcomingLoading, setUpcomingLoading] = useState<Set<string>>(new Set());

  const toggleExpense = useCallback(async (id: string) => {
    setExpandedExpenses((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      next.add(id);
      return next;
    });
    // Fetch only if not already loaded
    if (!upcomingData[id]) {
      setUpcomingLoading((prev) => new Set(prev).add(id));
      try {
        const result = await apiFetch(`/cash-flow/expenses/${id}/upcoming`);
        setUpcomingData((prev) => ({ ...prev, [id]: result }));
      } catch {
        setUpcomingData((prev) => ({ ...prev, [id]: [] }));
      } finally {
        setUpcomingLoading((prev) => { const n = new Set(prev); n.delete(id); return n; });
      }
    }
  }, [upcomingData]);

  // Modal state
  const [catModal, setCatModal] = useState<{ open: boolean; editing?: ExpenseCategory }>({ open: false });
  const [expModal, setExpModal] = useState<{ open: boolean; editing?: Expense }>({ open: false });
  const [deletingCat, setDeletingCat] = useState<ExpenseCategory | null>(null);
  const [deletingExp, setDeletingExp] = useState<Expense | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, exps] = await Promise.all([
        apiFetch("/cash-flow/categories"),
        apiFetch(`/cash-flow/expenses?includeInactive=${showInactive}`),
      ]);
      setCategories(cats);
      setExpenses(exps);
    } catch (e: any) {
      toast.error(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().split("T")[0];

  // Group expenses by frequency for display
  const filteredExpenses = expenses.filter((e) => {
    if (searchQuery.trim()) {
      if (
        !e.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !e.category?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      ) return false;
    }
    if (!showPastOneTime && e.frequency === "one-time" && e.startDate && e.startDate < today) {
      return false;
    }
    return true;
  });

  const grouped = FREQUENCY_ORDER.reduce<Record<string, Expense[]>>((acc, freq) => {
    const items = filteredExpenses.filter((e) => e.frequency === freq);
    if (items.length) acc[freq] = items;
    return acc;
  }, {});

  const totalMonthly = expenses
    .filter((e) => e.isActive && e.frequency === "monthly")
    .reduce((s, e) => s + Number(e.amount), 0);

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          label="Monthly Expenses"
          value={fmt(totalMonthly)}
          sub={`${expenses.filter((e) => e.isActive && e.frequency === "monthly").length} items`}
          color="red"
        />
        <SummaryCard
          label="Total Active Expenses"
          value={expenses.filter((e) => e.isActive).length.toString()}
          sub={`across ${categories.filter((c) => c.isActive).length} categories`}
          color="blue"
        />
        <SummaryCard
          label="Categories"
          value={categories.filter((c) => c.isActive).length.toString()}
          sub="active"
          color="purple"
        />
      </div>

      {/* Categories section */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-gray-500" />
            <h2 className="font-semibold text-gray-800">Categories</h2>
          </div>
          <button
            onClick={() => setCatModal({ open: true })}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 transition"
          >
            <Plus className="h-3.5 w-3.5" /> Add Category
          </button>
        </div>
        <div className="p-4 flex flex-wrap gap-2">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : categories.length === 0 ? (
            <p className="text-sm text-gray-400">No categories yet.</p>
          ) : (
            categories.map((cat) => (
              <CategoryChip
                key={cat.id}
                cat={cat}
                onEdit={() => setCatModal({ open: true, editing: cat })}
                onDelete={() => setDeletingCat(cat)}
              />
            ))
          )}
        </div>
      </div>

      {/* Expenses table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-gray-500" />
            <h2 className="font-semibold text-gray-800">Expenses</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search expenses…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 w-48"
              />
            </div>
            <label className="flex items-center gap-1.5 text-sm text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={showPastOneTime}
                onChange={(e) => setShowPastOneTime(e.target.checked)}
                className="rounded"
              />
              Show past one-time
            </label>
            <label className="flex items-center gap-1.5 text-sm text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded"
              />
              Show inactive
            </label>
            <button
              onClick={load}
              className="p-1.5 text-gray-400 hover:text-gray-700 transition"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={() => setExpModal({ open: true })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 transition"
            >
              <Plus className="h-3.5 w-3.5" /> Add Expense
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading…</div>
        ) : expenses.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">
            No expenses yet. Add your first expense above.
          </div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">
            No expenses match your search.
          </div>
        ) : (
          Object.entries(grouped).map(([freq, items]) => (
            <div key={freq}>
              <div className="px-5 py-2 bg-gray-50 border-y border-gray-100">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {FREQUENCY_LABELS[freq as Expense["frequency"]]}
                </span>
                <span className="ml-2 text-xs text-gray-400">
                  {fmt(items.reduce((s, e) => s + Number(e.amount), 0))} total
                </span>
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-50">
                  {items.map((exp) => (
                    <ExpenseRow
                      key={exp.id}
                      expense={exp}
                      expanded={expandedExpenses.has(exp.id)}
                      onToggle={() => toggleExpense(exp.id)}
                      upcoming={upcomingData[exp.id] ?? null}
                      loadingUpcoming={upcomingLoading.has(exp.id)}
                      onEdit={() => setExpModal({ open: true, editing: exp })}
                      onDelete={() => setDeletingExp(exp)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      {/* Modals */}
      {catModal.open && (
        <CategoryModal
          editing={catModal.editing}
          onClose={() => setCatModal({ open: false })}
          onSaved={load}
        />
      )}
      {expModal.open && (
        <ExpenseModal
          editing={expModal.editing}
          categories={categories}
          onClose={() => setExpModal({ open: false })}
          onSaved={load}
        />
      )}
      {deletingCat && (
        <ConfirmModal
          title="Delete Category"
          message={`Delete "${deletingCat.name}"? Expenses in this category will become uncategorized.`}
          onCancel={() => setDeletingCat(null)}
          onConfirm={async () => {
            try {
              await apiFetch(`/cash-flow/categories/${deletingCat.id}`, { method: "DELETE" });
              toast.success("Category deleted");
              setDeletingCat(null);
              load();
            } catch (e: any) { toast.error(e.message); }
          }}
        />
      )}
      {deletingExp && (
        <ConfirmModal
          title="Delete Expense"
          message={`Delete "${deletingExp.name}"? This cannot be undone.`}
          onCancel={() => setDeletingExp(null)}
          onConfirm={async () => {
            try {
              await apiFetch(`/cash-flow/expenses/${deletingExp.id}`, { method: "DELETE" });
              toast.success("Expense deleted");
              setDeletingExp(null);
              load();
            } catch (e: any) { toast.error(e.message); }
          }}
        />
      )}
    </div>
  );
}

// ─── Small components ─────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, color }: { label: string; value: string; sub: string; color: string }) {
  const colors: Record<string, string> = {
    red: "bg-red-50 border-red-100 text-red-700",
    blue: "bg-blue-50 border-blue-100 text-blue-700",
    purple: "bg-purple-50 border-purple-100 text-purple-700",
  };
  return (
    <div className={`rounded-lg border px-5 py-4 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      <p className="text-xs opacity-60 mt-0.5">{sub}</p>
    </div>
  );
}

function CategoryChip({ cat, onEdit, onDelete }: { cat: ExpenseCategory; onEdit: () => void; onDelete: () => void }) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm"
      style={{ borderColor: cat.color || "#E5E7EB", backgroundColor: `${cat.color || "#E5E7EB"}18` }}
    >
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cat.color || "#9CA3AF" }} />
      <span className="font-medium text-gray-700">{cat.name}</span>
      <button onClick={onEdit} className="text-gray-400 hover:text-gray-700 ml-1"><Pencil className="h-3 w-3" /></button>
      <button onClick={onDelete} className="text-gray-400 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
    </div>
  );
}

function ExpenseRow({
  expense, expanded, onToggle, upcoming, loadingUpcoming, onEdit, onDelete,
}: {
  expense: Expense;
  expanded: boolean;
  onToggle: () => void;
  upcoming: UpcomingOccurrence[] | null;
  loadingUpcoming: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const fmtOccurrenceDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  };

  return (
    <>
      <tr className={`hover:bg-gray-50 transition ${!expense.isActive ? "opacity-50" : ""}`}>
        <td className="px-5 py-3 w-8">
          {expense.category && (
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: expense.category.color || "#9CA3AF" }}
              title={expense.category.name}
            />
          )}
        </td>
        <td className="py-3 pr-4">
          <button
            onClick={onToggle}
            className="flex items-center gap-1.5 text-left group"
          >
            {expanded
              ? <ChevronDown className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
              : <ChevronRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-emerald-500 flex-shrink-0" />
            }
            <div>
              <div className="font-medium text-gray-900 group-hover:text-emerald-700 transition">{expense.name}</div>
              {expense.category && <div className="text-xs text-gray-400">{expense.category.name}</div>}
            </div>
          </button>
        </td>
        <td className="py-3 pr-4 text-gray-500 text-xs">{billScheduleLabel(expense)}</td>
        <td className="py-3 pr-4 font-semibold text-gray-900 text-right">{fmt(Number(expense.amount))}</td>
        <td className="py-3 pr-5 text-right">
          <div className="flex items-center justify-end gap-2">
            {!expense.isActive && (
              <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Inactive</span>
            )}
            <button onClick={onEdit} className="text-gray-400 hover:text-gray-700 transition"><Pencil className="h-4 w-4" /></button>
            <button onClick={onDelete} className="text-gray-400 hover:text-red-500 transition"><Trash2 className="h-4 w-4" /></button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} className="px-5 pb-4 pt-0 bg-gray-50 border-b border-gray-100">
            <div className="ml-6 mt-2 rounded-lg border border-gray-200 bg-white overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-2">
                <CalendarDays className="h-3.5 w-3.5 text-emerald-500" />
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Upcoming Payments — Next 6 Months
                </span>
              </div>
              {loadingUpcoming ? (
                <div className="flex items-center justify-center gap-2 py-5 text-sm text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : !upcoming || upcoming.length === 0 ? (
                <div className="py-5 text-center text-sm text-gray-400">
                  No scheduled payments in the next 6 months.
                </div>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {upcoming.map((occ, i) => (
                    <li key={i} className="flex items-center justify-between px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-800">{fmtOccurrenceDate(occ.date)}</span>
                        {occ.isOverridden && occ.originalDate && (
                          <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-full">
                            moved from {fmtOccurrenceDate(occ.originalDate)}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{fmt(occ.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Category Modal ───────────────────────────────────────────────────────────

const PRESET_COLORS = [
  "#10B981","#3B82F6","#8B5CF6","#F59E0B","#EF4444",
  "#F97316","#14B8A6","#6B7280","#EC4899","#84CC16",
];

function CategoryModal({ editing, onClose, onSaved }: { editing?: ExpenseCategory; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState(editing ? { name: editing.name, description: editing.description || "", color: editing.color || "#3B82F6" } : blankCategory());
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await apiFetch(`/cash-flow/categories/${editing.id}`, { method: "PATCH", body: JSON.stringify(form) });
        toast.success("Category updated");
      } else {
        await apiFetch("/cash-flow/categories", { method: "POST", body: JSON.stringify(form) });
        toast.success("Category created");
      }
      onSaved();
      onClose();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={editing ? "Edit Category" : "New Category"} onClose={onClose}>
      <div className="space-y-4">
        <Field label="Name">
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Payroll" />
        </Field>
        <Field label="Description (optional)">
          <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" />
        </Field>
        <Field label="Color">
          <div className="flex gap-2 flex-wrap">
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setForm({ ...form, color: c })}
                className="h-7 w-7 rounded-full border-2 transition flex items-center justify-center"
                style={{ backgroundColor: c, borderColor: form.color === c ? "#1F2937" : "transparent" }}
              >
                {form.color === c && <Check className="h-3.5 w-3.5 text-white" />}
              </button>
            ))}
          </div>
        </Field>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition">Cancel</button>
        <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 transition">{saving ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}

// ─── Expense Modal ────────────────────────────────────────────────────────────

function ExpenseModal({ editing, categories, onClose, onSaved }: {
  editing?: Expense;
  categories: ExpenseCategory[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Partial<Expense>>(editing ? { ...editing } : blankExpense());
  const [saving, setSaving] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    if (form.amount == null || isNaN(Number(form.amount))) { toast.error("Amount is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount), endDate: form.endDate ?? null };
      if (editing) {
        await apiFetch(`/cash-flow/expenses/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        toast.success("Expense updated");
      } else {
        await apiFetch("/cash-flow/expenses", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Expense created");
      }
      onSaved();
      onClose();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const selectedCat = categories.find((c) => c.id === form.categoryId);

  return (
    <Modal title={editing ? "Edit Expense" : "New Expense"} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-4">
        {/* Name */}
        <div className="col-span-2">
          <Field label="Expense Name">
            <input className="input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Office Rent" />
          </Field>
        </div>

        {/* Amount */}
        <Field label="Amount ($)">
          <input className="input" type="number" min="0" step="0.01" value={form.amount ?? ""} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) })} placeholder="0.00" />
        </Field>

        {/* Category */}
        <Field label="Category">
          <div className="relative">
            <button
              onClick={() => setCatOpen(!catOpen)}
              className="input flex items-center justify-between w-full text-left"
            >
              <span className={selectedCat ? "text-gray-900" : "text-gray-400"}>
                {selectedCat ? selectedCat.name : "Select category…"}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>
            {catOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-auto">
                <button className="w-full px-3 py-2 text-left text-sm text-gray-400 hover:bg-gray-50" onClick={() => { setForm({ ...form, categoryId: undefined }); setCatOpen(false); }}>None</button>
                {categories.map((c) => (
                  <button key={c.id} className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50" onClick={() => { setForm({ ...form, categoryId: c.id }); setCatOpen(false); }}>
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color || "#9CA3AF" }} />
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Field>

        {/* Frequency */}
        <Field label="Frequency">
          <select className="input" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value as Expense["frequency"] })}>
            {FREQUENCY_ORDER.map((f) => <option key={f} value={f}>{FREQUENCY_LABELS[f]}</option>)}
          </select>
        </Field>

        {/* Bill Day */}
        {(form.frequency === "monthly" || form.frequency === "quarterly" || form.frequency === "yearly") && (
          <Field label={form.frequency === "yearly" ? "Day of Month" : "Bill Day (1–31)"}>
            <input className="input" type="number" min="1" max="31" value={form.billDay ?? ""} onChange={(e) => setForm({ ...form, billDay: parseInt(e.target.value) || undefined })} placeholder="e.g. 1" />
          </Field>
        )}

        {form.frequency === "weekly" && (
          <Field label="Day of Week">
            <select className="input" value={form.billDay ?? ""} onChange={(e) => setForm({ ...form, billDay: parseInt(e.target.value) })}>
              <option value="">Select…</option>
              {DAY_OF_WEEK.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </Field>
        )}

        {(form.frequency === "quarterly" || form.frequency === "yearly") && (
          <Field label="Month">
            <select className="input" value={form.billMonth ?? ""} onChange={(e) => setForm({ ...form, billMonth: parseInt(e.target.value) || undefined })}>
              <option value="">Select…</option>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </Field>
        )}

        {/* Start / End date */}
        <Field label="Start Date (optional)">
          <input className="input" type="date" value={form.startDate || ""} onChange={(e) => setForm({ ...form, startDate: e.target.value || undefined })} />
        </Field>
        <Field label="End Date (optional)">
          <div className="flex items-center gap-1">
            <input className="input flex-1" type="date" value={form.endDate || ""} onChange={(e) => setForm({ ...form, endDate: e.target.value || undefined })} />
            {form.endDate && (
              <button type="button" onClick={() => setForm({ ...form, endDate: undefined })} className="text-gray-400 hover:text-gray-600 px-1" title="Clear end date">✕</button>
            )}
          </div>
        </Field>

        {/* Notes */}
        <div className="col-span-2">
          <Field label="Notes (optional)">
            <textarea className="input resize-none" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional details…" />
          </Field>
        </div>

        {/* Active toggle (edit only) */}
        {editing && (
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="isActive" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="rounded" />
            <label htmlFor="isActive" className="text-sm text-gray-600">Active (appears in cash flow grid)</label>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition">Cancel</button>
        <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 transition">{saving ? "Saving…" : "Save"}</button>
      </div>
    </Modal>
  );
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className={`bg-white rounded-xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

// ─── Payroll & Contractor Pay Projections ─────────────────────────────────────

interface DayEntry {
  date: string;
  estimatedHours: number;
  hoursUsed: number;
  actualHours?: number;
  clockInAt?: string;
  clockOutAt?: string;
}

interface ReconciliationConfirmation {
  id: string;
  teamMemberId: string;
  name: string | null;
  periodKey: string;
  projectedAmount: number;
  actualAmount: number | null;
  payDate: string;
  isConfirmed: boolean;
  confirmedAt: string | null;
}

interface ReconciliationPM {
  balance: number;
  notes: string | null;
  projectedTotal: number;
  confirmedTotal: number;
  remainingBalance: number;
  confirmations: ReconciliationConfirmation[];
}

interface StandardPayrollMember {
  id: string;
  name: string;
  employmentType: string;
  role?: string;
  timezone?: string;
  paymentMethod?: string;
  shiftStart: string;
  shiftEnd: string;
  breakMinutes: number;
  lunchTime?: string | null;
  hoursPerDay: number;
  hourlyRate: number;
  period1: { workingDays: number; hours: number; amount: number; payDate: string; days: DayEntry[] };
  period2: { workingDays: number; hours: number; amount: number; payDate: string; days: DayEntry[] };
}

interface UpworkPayrollMember {
  id: string;
  name: string;
  employmentType: string;
  role?: string;
  timezone?: string;
  shiftStart: string;
  shiftEnd: string;
  breakMinutes: number;
  lunchTime?: string | null;
  hoursPerDay: number;
  hourlyRate: number;
  weeks: Array<{ weekLabel: string; workingDays: number; hours: number; amount: number; payDate: string; days: DayEntry[] }>;
  total: number;
}

interface PayrollData {
  year: number;
  month: number;
  daysInMonth: number;
  period1: { label: string; total: number };
  period2: { label: string; total: number };
  members: StandardPayrollMember[];
  upwork: UpworkPayrollMember[];
  grandTotal: number;
}

const ROLE_LABELS: Record<string, string> = {
  pcc: "PCC",
  team_lead: "Team Lead",
  operations_manager: "Operations Manager",
  kpi: "KPI",
  upper_management: "Upper Management",
  qa: "QA",
};

const EMP_TYPE_STYLES: Record<string, string> = {
  "full-time":  "bg-blue-100 text-blue-700",
  "part-time":  "bg-yellow-100 text-yellow-700",
  "contractor": "bg-purple-100 text-purple-700",
};

const PM_STYLES: Record<string, string> = {
  quickbooks: "bg-green-100 text-green-700",
  payoneer:   "bg-orange-100 text-orange-700",
  wise:       "bg-teal-100 text-teal-700",
  paypal:     "bg-blue-100 text-blue-700",
  veem:       "bg-violet-100 text-violet-700",
  upwork:     "bg-emerald-100 text-emerald-700",
};

const PM_LABELS: Record<string, string> = {
  quickbooks: "QuickBooks",
  payoneer:   "Payoneer",
  wise:       "Wise",
  paypal:     "PayPal",
  veem:       "Veem",
  upwork:     "Upwork",
  __none__:   "No Payment Method",
};

const PM_ORDER = ["quickbooks", "wise", "payoneer", "paypal", "veem", "__none__"];

function fmtPayDate(dateStr: string): string {
  const [y, mo, d] = dateStr.split("-").map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtTime(hhmm: string): string {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
}


function PayrollTab() {
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data,  setData]  = useState<PayrollData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [collapsed,       setCollapsed]       = useState<Set<string>>(new Set());
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const [recon,           setRecon]           = useState<Record<string, ReconciliationPM>>({});

  const toggleSection = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleMember = (id: string) =>
    setExpandedMembers((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const MONTH_NAMES = ["January","February","March","April","May","June",
                       "July","August","September","October","November","December"];

  const loadRecon = useCallback(async (y: number, m: number) => {
    try {
      const result = await apiFetch(`/cash-flow/payroll-reconciliation?year=${y}&month=${m}`);
      setRecon(result as Record<string, ReconciliationPM>);
    } catch { /* reconciliation is non-critical — silently ignore */ }
  }, []);

  const load = useCallback(async (y: number, m: number) => {
    setLoading(true);
    setError(null);
    try {
      const [projResult] = await Promise.all([
        apiFetch(`/payroll/payroll-projections?year=${y}&month=${m}`),
        loadRecon(y, m),
      ]);
      setData(projResult);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load payroll projections");
    } finally {
      setLoading(false);
    }
  }, [loadRecon]);

  useEffect(() => { void load(year, month); }, [load, year, month]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  // ── Reconciliation panel — balance input + stats bar ──────────────────────
  const ReconciliationPanel = ({ pmKey, projectedTotal }: { pmKey: string; projectedTotal: number }) => {
    const pmRecon = recon[pmKey];
    const balance        = pmRecon?.balance       ?? 0;
    const confirmedTotal = pmRecon?.confirmedTotal ?? 0;
    const remainingBal   = Math.max(0, balance - confirmedTotal);
    const netCashNeeded  = Math.max(0, projectedTotal - Math.max(0, balance) - confirmedTotal);

    const [balInput, setBalInput] = useState(balance.toFixed(2));
    const [saving,   setSaving]   = useState(false);

    useEffect(() => { setBalInput((pmRecon?.balance ?? 0).toFixed(2)); }, [pmRecon?.balance]);

    const saveBalance = async () => {
      const val = parseFloat(balInput);
      if (isNaN(val) || val < 0) { setBalInput(balance.toFixed(2)); return; }
      setSaving(true);
      try {
        await apiFetch("/cash-flow/payroll-reconciliation/balance", {
          method: "PUT",
          body: JSON.stringify({ paymentMethod: pmKey, year, month, balance: val }),
        });
        await loadRecon(year, month);
      } catch { toast.error("Failed to save balance"); setBalInput(balance.toFixed(2)); }
      finally  { setSaving(false); }
    };

    return (
      <div className="mb-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account Balance</span>
          <div className="flex items-center gap-1">
            <span className="text-gray-400 text-xs">$</span>
            <input
              type="number" min="0" step="0.01"
              value={balInput}
              onChange={(e) => setBalInput(e.target.value)}
              onBlur={saveBalance}
              onKeyDown={(e) => e.key === "Enter" && saveBalance()}
              disabled={saving}
              className="w-28 text-sm font-semibold text-gray-800 border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-60"
            />
          </div>
        </div>
        <div className="text-xs text-gray-500">
          Confirmed Paid: <span className="font-semibold text-gray-700">{fmt(confirmedTotal)}</span>
        </div>
        <div className="text-xs text-gray-500">
          Remaining Balance: <span className="font-semibold text-gray-700">{fmt(remainingBal)}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Net Cash Needed</span>
          <span className={`text-sm font-bold ${netCashNeeded > 0 ? "text-red-600" : "text-green-600"}`}>
            {fmt(netCashNeeded)}
          </span>
        </div>
      </div>
    );
  };

  // ── Confirm checkbox cell — per member per period ──────────────────────────
  const ConfirmCell = ({ member, pmKey, periodKey }: {
    member: StandardPayrollMember;
    pmKey: string;
    periodKey: "p1" | "p2";
  }) => {
    const period = periodKey === "p1" ? member.period1 : member.period2;
    const conf   = recon[pmKey]?.confirmations.find(
      (c) => c.teamMemberId === member.id && c.periodKey === periodKey
    );
    const isConfirmed = conf?.isConfirmed ?? false;

    const [editing,      setEditing]      = useState(false);
    const [actualInput,  setActualInput]  = useState((conf?.actualAmount ?? period.amount).toFixed(2));
    const [saving,       setSaving]       = useState(false);

    useEffect(() => {
      setActualInput((conf?.actualAmount ?? period.amount).toFixed(2));
    }, [conf?.actualAmount, period.amount]);

    const doConfirm = async (checked: boolean, amt?: number) => {
      setSaving(true);
      try {
        await apiFetch("/cash-flow/payroll-reconciliation/confirm", {
          method: "POST",
          body: JSON.stringify({
            teamMemberId:    member.id,
            paymentMethod:   pmKey,
            year, month,
            periodKey,
            projectedAmount: period.amount,
            payDate:         period.payDate,
            isConfirmed:     checked,
            actualAmount:    checked ? (amt ?? parseFloat(actualInput)) : undefined,
          }),
        });
        await loadRecon(year, month);
        if (checked) setEditing(false);
      } catch { toast.error("Failed to update payment"); }
      finally  { setSaving(false); }
    };

    return (
      <div className="flex flex-col items-end gap-0.5 mt-1.5">
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isConfirmed}
            disabled={saving}
            onChange={(e) => { if (e.target.checked) setEditing(true); else doConfirm(false); }}
            className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
          />
          <span className={`text-xs ${isConfirmed ? "text-green-600 font-medium" : "text-gray-400"}`}>
            {isConfirmed ? "Paid" : "Mark paid"}
          </span>
        </label>
        {(editing || isConfirmed) && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">$</span>
            <input
              type="number" min="0" step="0.01"
              value={actualInput}
              onChange={(e) => setActualInput(e.target.value)}
              disabled={saving || (isConfirmed && !editing)}
              onClick={() => isConfirmed && setEditing(true)}
              className="w-20 text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-green-400 disabled:bg-transparent disabled:border-transparent disabled:text-green-700 disabled:font-medium cursor-pointer"
            />
            {editing && (
              <>
                <button
                  onClick={() => doConfirm(true, parseFloat(actualInput))}
                  disabled={saving}
                  className="text-xs px-1.5 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >✓</button>
                <button onClick={() => { setEditing(false); setActualInput((conf?.actualAmount ?? period.amount).toFixed(2)); }}
                  className="text-xs text-gray-400 hover:text-gray-600">✕</button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Per-member hours breakdown row ─────────────────────────────────────────
  const HoursBreakdownRow = ({ member, colSpan }: { member: StandardPayrollMember; colSpan: number }) => {
    const todayStr = new Date().toISOString().slice(0, 10);

    const daySource = (d: DayEntry): { label: string; cls: string } => {
      if (d.actualHours !== undefined && d.actualHours > 0) return { label: "· Clocked",  cls: "text-green-600" };
      if (d.actualHours === 0)                               return { label: "· No Entry", cls: "text-red-400"   };
      if (d.date > todayStr)                                 return { label: "· Projected", cls: "text-blue-500" };
      return                                                        { label: "· Estimated", cls: "text-gray-400" };
    };

    const fmtClockTime = (iso: string, tz?: string): string =>
      new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz || "America/Los_Angeles" });

    const PeriodDays = ({ days, label, headerCls, tz, rate }: { days: DayEntry[]; label: string; headerCls: string; tz?: string; rate: number }) => {
      const totalSched  = days.reduce((s, d) => s + d.estimatedHours, 0);
      const totalActual = days.reduce((s, d) => s + (d.actualHours !== undefined ? d.actualHours : d.estimatedHours), 0);
      // Diff = Actual − Sched: negative = worked less than scheduled (red), positive = worked extra (amber)
      const totalDiffH   = days.reduce((s, d) => s + (d.actualHours !== undefined ? d.actualHours - d.estimatedHours : 0), 0);
      const totalDiffAmt = totalDiffH * rate;
      return (
        <div>
          <div className={`text-xs font-semibold mb-1.5 ${headerCls}`}>{label}</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100">
                <th className="text-left pb-1 pr-3 font-medium">Date</th>
                <th className="text-right pb-1 pr-3 font-medium">Sched</th>
                <th className="text-right pb-1 pr-3 font-medium">Actual</th>
                <th className="text-right pb-1 pr-3 font-medium">Diff</th>
                <th className="text-right pb-1 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => {
                const src = daySource(d);
                const [dy, dm, dd] = d.date.split("-").map(Number);
                const lbl = new Date(dy, dm - 1, dd).toLocaleDateString("en-US", { month: "short", day: "numeric" });
                const hasDiff = d.actualHours !== undefined;
                const diffH   = hasDiff ? d.actualHours! - d.estimatedHours : null;
                const diffAmt = diffH !== null ? diffH * rate : null;
                const diffCls = diffH === null ? "" : diffH < 0 ? "text-red-500" : diffH > 0 ? "text-amber-500" : "text-emerald-600";
                return (
                  <tr key={d.date} className="border-b border-gray-50 hover:bg-white/60">
                    <td className="py-0.5 pr-3 text-gray-700">{lbl}</td>
                    <td className="text-right pr-3 text-gray-400">{d.estimatedHours}h</td>
                    <td className="text-right pr-3 font-medium text-gray-700">
                      {d.actualHours !== undefined ? (
                        d.actualHours > 0 ? (
                          <span>
                            {d.actualHours}h
                            {d.clockInAt && d.clockOutAt && (
                              <span className="ml-1 text-gray-400 font-normal">
                                ({fmtClockTime(d.clockInAt, tz)} – {fmtClockTime(d.clockOutAt, tz)})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-red-400">0h</span>
                        )
                      ) : (
                        <span>{d.hoursUsed}h</span>
                      )}
                    </td>
                    <td className={`text-right pr-3 font-medium ${diffCls}`}>
                      {diffH !== null ? (
                        diffH === 0 ? <span className="text-emerald-600">On schedule</span> : (
                          <span>
                            {diffH > 0 ? "+" : ""}{diffH.toFixed(1)}h
                            <span className="ml-1 font-normal">
                              ({diffH > 0 ? "+" : "-"}${Math.abs(diffAmt!).toFixed(2)})
                            </span>
                          </span>
                        )
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className={`text-right ${src.cls}`}>{src.label}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 font-semibold text-gray-700">
                <td className="pt-1">Total</td>
                <td className="text-right pr-3 text-gray-400 pt-1">{totalSched.toFixed(1)}h</td>
                <td className="text-right pr-3 pt-1">{totalActual.toFixed(1)}h</td>
                <td className={`text-right pr-3 pt-1 ${totalDiffH < 0 ? "text-red-500" : totalDiffH > 0 ? "text-amber-500" : "text-emerald-600"}`}>
                  {totalDiffH === 0 ? <span className="text-emerald-600">On schedule</span> : (
                    <span>
                      {totalDiffH > 0 ? "+" : ""}{totalDiffH.toFixed(1)}h
                      <span className="ml-1 font-normal">
                        ({totalDiffH > 0 ? "+" : "-"}${Math.abs(totalDiffAmt).toFixed(2)})
                      </span>
                    </span>
                  )}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      );
    };

    const hasDays = member.period1.days.length > 0 || member.period2.days.length > 0;
    if (!hasDays) return null;

    return (
      <tr className="bg-indigo-50/30 border-b border-indigo-100">
        <td colSpan={colSpan} className="px-5 py-3">
          <div className={`grid gap-8 ${member.period1.days.length > 0 && member.period2.days.length > 0 ? "grid-cols-2" : "grid-cols-1 max-w-sm"}`}>
            {member.period1.days.length > 0 && (
              <PeriodDays days={member.period1.days} label={`Period 1 — ${member.period1.days.length} day${member.period1.days.length !== 1 ? "s" : ""}`} headerCls="text-blue-600" tz={member.timezone} rate={member.hourlyRate} />
            )}
            {member.period2.days.length > 0 && (
              <PeriodDays days={member.period2.days} label={`Period 2 — ${member.period2.days.length} day${member.period2.days.length !== 1 ? "s" : ""}`} headerCls="text-indigo-600" tz={member.timezone} rate={member.hourlyRate} />
            )}
          </div>
        </td>
      </tr>
    );
  };

  const StandardTable = ({ members, period1Label, period2Label, pmKey }: {
    members: StandardPayrollMember[];
    period1Label: string;
    period2Label: string;
    pmKey: string;
  }) => {
    const p1Total = members.reduce((s, m) => s + m.period1.amount, 0);
    const p2Total = members.reduce((s, m) => s + m.period2.amount, 0);
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {/* expand chevron column */}
                <th className="w-8 px-2" rowSpan={2} />
                <th className="px-5 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" rowSpan={2}>Name / Schedule</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide" rowSpan={2}>Type</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide" rowSpan={2}>Rate</th>
                <th colSpan={3} className="px-3 py-2 text-center text-xs font-semibold text-blue-600 uppercase tracking-wide border-l border-gray-200">
                  Period 1 ({period1Label})
                </th>
                <th colSpan={3} className="px-3 py-2 text-center text-xs font-semibold text-indigo-600 uppercase tracking-wide border-l border-gray-200">
                  Period 2 ({period2Label})
                </th>
                <th className="px-5 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide border-l border-gray-200" rowSpan={2}>Monthly Total</th>
              </tr>
              <tr className="border-b border-gray-200 bg-gray-50/80 text-xs text-gray-400 font-medium uppercase tracking-wide">
                <th className="px-3 py-2 text-right border-l border-gray-200">Days</th>
                <th className="px-3 py-2 text-right">Paid Hrs</th>
                <th className="px-3 py-2 text-right">Pay Date · Amount</th>
                <th className="px-3 py-2 text-right border-l border-gray-200">Days</th>
                <th className="px-3 py-2 text-right">Paid Hrs</th>
                <th className="px-3 py-2 text-right">Pay Date · Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {members.map((m) => {
                const isExpanded = expandedMembers.has(m.id);
                const hasDays = m.period1.days.length > 0 || m.period2.days.length > 0;
                return (
                  <React.Fragment key={m.id}>
                    <tr className="hover:bg-gray-50/50 transition-colors">
                      {/* expand chevron */}
                      <td className="px-2 py-3 text-center">
                        {hasDays && (
                          <button
                            onClick={() => toggleMember(m.id)}
                            className="p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3 min-w-[220px]">
                        <div className="font-medium text-gray-900">{m.name}</div>
                        {m.role && <div className="text-xs text-gray-400 mt-0.5">{ROLE_LABELS[m.role] ?? m.role}</div>}
                        <div className="text-xs text-gray-400 mt-0.5">
                          {m.hoursPerDay > 0 ? (
                            <>
                              {fmtTime(m.shiftStart)} – {fmtTime(m.shiftEnd)}
                              {m.lunchTime && ` · 30 min lunch (unpaid)`}
                              {m.breakMinutes > 0 && ` · ${m.breakMinutes} min break (paid)`}
                              {` · ${m.hoursPerDay} paid hrs/day`}
                            </>
                          ) : (
                            <>Per-day schedule · {(m.period1.hours + m.period2.hours).toFixed(1)} paid hrs/mo</>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${EMP_TYPE_STYLES[m.employmentType] ?? "bg-gray-100 text-gray-600"}`}>
                          {m.employmentType}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700 whitespace-nowrap">{fmt(m.hourlyRate)}/hr</td>
                      {/* Period 1 */}
                      <td className="px-3 py-3 text-right text-gray-700 border-l border-gray-100">{m.period1.workingDays}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{m.period1.hours}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="text-xs text-gray-400">{fmtPayDate(m.period1.payDate)}</div>
                        <div className="font-semibold text-gray-900">{fmt(m.period1.amount)}</div>
                        <ConfirmCell member={m} pmKey={pmKey} periodKey="p1" />
                      </td>
                      {/* Period 2 */}
                      <td className="px-3 py-3 text-right text-gray-700 border-l border-gray-100">{m.period2.workingDays}</td>
                      <td className="px-3 py-3 text-right text-gray-700">{m.period2.hours}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <div className="text-xs text-gray-400">{fmtPayDate(m.period2.payDate)}</div>
                        <div className="font-semibold text-gray-900">{fmt(m.period2.amount)}</div>
                        <ConfirmCell member={m} pmKey={pmKey} periodKey="p2" />
                      </td>
                      {/* Monthly total */}
                      <td className="px-5 py-3 text-right font-bold text-gray-900 border-l border-gray-100 whitespace-nowrap">
                        {fmt(m.period1.amount + m.period2.amount)}
                      </td>
                    </tr>
                    {isExpanded && <HoursBreakdownRow member={m} colSpan={11} />}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50 text-sm font-semibold text-gray-700">
                <td colSpan={4} className="px-5 py-3">Subtotal</td>
                <td colSpan={3} className="px-3 py-3 text-right border-l border-gray-200">
                  <span className="text-blue-700">{fmt(p1Total)}</span>
                </td>
                <td colSpan={3} className="px-3 py-3 text-right border-l border-gray-200">
                  <span className="text-indigo-700">{fmt(p2Total)}</span>
                </td>
                <td className="px-5 py-3 text-right text-base font-bold text-gray-900 border-l border-gray-200">
                  {fmt(p1Total + p2Total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // ── Per-week confirm cell for Upwork ─────────────────────────────────────────
  const UpworkConfirmCell = ({ member, weekIndex, week }: {
    member: UpworkPayrollMember;
    weekIndex: number;
    week: UpworkPayrollMember["weeks"][number];
  }) => {
    const pmKey     = "upwork";
    const periodKey = `week-${weekIndex}`;
    const conf      = recon[pmKey]?.confirmations.find(
      (c) => c.teamMemberId === member.id && c.periodKey === periodKey
    );
    const isConfirmed = conf?.isConfirmed ?? false;

    const [editing,     setEditing]     = useState(false);
    const [actualInput, setActualInput] = useState((conf?.actualAmount ?? week.amount).toFixed(2));
    const [saving,      setSaving]      = useState(false);

    useEffect(() => {
      setActualInput((conf?.actualAmount ?? week.amount).toFixed(2));
    }, [conf?.actualAmount, week.amount]);

    const doConfirm = async (checked: boolean, amt?: number) => {
      setSaving(true);
      try {
        await apiFetch("/cash-flow/payroll-reconciliation/confirm", {
          method: "POST",
          body: JSON.stringify({
            teamMemberId:    member.id,
            paymentMethod:   pmKey,
            year, month,
            periodKey,
            projectedAmount: week.amount,
            payDate:         week.payDate,
            isConfirmed:     checked,
            actualAmount:    checked ? (amt ?? parseFloat(actualInput)) : undefined,
          }),
        });
        await loadRecon(year, month);
        if (checked) setEditing(false);
      } catch { toast.error("Failed to update payment"); }
      finally  { setSaving(false); }
    };

    return (
      <div className="flex flex-col items-end gap-0.5 mt-1.5">
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isConfirmed}
            disabled={saving}
            onChange={(e) => { if (e.target.checked) setEditing(true); else doConfirm(false); }}
            className="h-3.5 w-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
          />
          <span className={`text-xs ${isConfirmed ? "text-green-600 font-medium" : "text-gray-400"}`}>
            {isConfirmed ? "Paid" : "Mark paid"}
          </span>
        </label>
        {(editing || isConfirmed) && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">$</span>
            <input
              type="number" min="0" step="0.01"
              value={actualInput}
              onChange={(e) => setActualInput(e.target.value)}
              disabled={saving || (isConfirmed && !editing)}
              onClick={() => isConfirmed && setEditing(true)}
              className="w-20 text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-green-400 disabled:bg-transparent disabled:border-transparent disabled:text-green-700 disabled:font-medium cursor-pointer"
            />
            {editing && (
              <>
                <button
                  onClick={() => doConfirm(true, parseFloat(actualInput))}
                  disabled={saving}
                  className="text-xs px-1.5 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >✓</button>
                <button onClick={() => { setEditing(false); setActualInput((conf?.actualAmount ?? week.amount).toFixed(2)); }}
                  className="text-xs text-gray-400 hover:text-gray-600">✕</button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const UpworkCard = ({ member }: { member: UpworkPayrollMember }) => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-3 flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-gray-800">{member.name}</span>
          {member.role && <span className="ml-2 text-xs text-gray-500">{ROLE_LABELS[member.role] ?? member.role}</span>}
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">upwork</span>
        <div className="text-xs text-gray-400 mt-0.5">
          {member.hoursPerDay > 0 ? (
            <>
              {fmtTime(member.shiftStart)} – {fmtTime(member.shiftEnd)}
              {member.lunchTime && ` · 30 min lunch (unpaid)`}
              {member.breakMinutes > 0 && ` · ${member.breakMinutes} min break (paid)`}
              {` · ${member.hoursPerDay} paid hrs/day`}
            </>
          ) : (
            <>Per-day schedule · {member.weeks.reduce((s, w) => s + w.hours, 0).toFixed(1)} paid hrs/mo</>
          )}
        </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-400">Monthly total</div>
          <div className="text-sm font-semibold text-gray-800">{fmt(member.total)}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Week</th>
              <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Days</th>
              <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Hours</th>
              <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Pay Date</th>
              <th className="px-5 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {member.weeks.map((w, i) => (
              <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-5 py-3 text-gray-800">{w.weekLabel}</td>
                <td className="px-3 py-3 text-right text-gray-700">{w.workingDays}</td>
                <td className="px-3 py-3 text-right text-gray-700">{w.hours}</td>
                <td className="px-3 py-3 text-right text-gray-600 text-xs">{fmtPayDate(w.payDate)}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <div className="font-semibold text-gray-900">{fmt(w.amount)}</div>
                  <UpworkConfirmCell member={member} weekIndex={i} week={w} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50">
              <td colSpan={4} className="px-5 py-3 text-sm font-semibold text-gray-700">Total</td>
              <td className="px-5 py-3 text-right text-base font-bold text-gray-900">{fmt(member.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Month navigator */}
      <div className="flex items-center gap-3">
        <button onClick={prevMonth} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-base font-semibold text-gray-800 w-44 text-center">
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <button onClick={nextMonth} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading projections…
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && data && (
        <>
          {/* Grand total banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-emerald-700">Total Projected Payroll — {MONTH_NAMES[month - 1]} {year}</div>
              <div className="text-xs text-emerald-500 mt-0.5">
                {data.members.length + data.upwork.length} hourly member{(data.members.length + data.upwork.length) !== 1 ? "s" : ""}
                {data.upwork.length > 0 && ` (${data.upwork.length} Upwork)`}
              </div>
            </div>
            <div className="text-2xl font-bold text-emerald-700">{fmt(data.grandTotal)}</div>
          </div>

          {data.members.length === 0 && data.upwork.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              No active hourly team members with a schedule and rate configured.
            </div>
          ) : (
            <>
              {/* Standard bi-monthly — grouped by payment method */}
              {data.members.length > 0 && (() => {
                const groupMap = new Map<string, StandardPayrollMember[]>();
                for (const m of data.members) {
                  const key = m.paymentMethod ?? "__none__";
                  if (!groupMap.has(key)) groupMap.set(key, []);
                  groupMap.get(key)!.push(m);
                }
                const groups: Array<{ key: string; members: StandardPayrollMember[] }> = [];
                for (const pm of PM_ORDER) {
                  if (groupMap.has(pm)) groups.push({ key: pm, members: groupMap.get(pm)! });
                }
                for (const [k, v] of groupMap) {
                  if (!PM_ORDER.includes(k)) groups.push({ key: k, members: v });
                }
                return (
                  <div className="space-y-4">
                    {groups.map(({ key, members: gm }) => {
                      const label = PM_LABELS[key] ?? key;
                      const groupTotal = gm.reduce((s, m) => s + m.period1.amount + m.period2.amount, 0);
                      const isCollapsed = collapsed.has(key);
                      return (
                        <div key={key}>
                          <button
                            onClick={() => toggleSection(key)}
                            className="w-full flex items-center gap-2 mb-2 group"
                          >
                            <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${PM_STYLES[key] ?? "bg-gray-100 text-gray-600"}`}>
                              {label}
                            </span>
                            <span className="text-xs text-gray-400">{gm.length} member{gm.length !== 1 ? "s" : ""}</span>
                            <span className="ml-auto text-sm font-semibold text-gray-700">{fmt(groupTotal)}</span>
                          </button>
                          {!isCollapsed && (
                            <>
                              <ReconciliationPanel pmKey={key} projectedTotal={groupTotal} />
                              <StandardTable members={gm} period1Label={data.period1.label} period2Label={data.period2.label} pmKey={key} />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Upwork weekly section */}
              {data.upwork.length > 0 && (() => {
                const isCollapsed = collapsed.has("__upwork__");
                return (
                  <div>
                    <button
                      onClick={() => toggleSection("__upwork__")}
                      className="w-full flex items-center gap-2 mb-2 group"
                    >
                      <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        Upwork
                      </span>
                      <span className="text-xs text-gray-400">{data.upwork.length} member{data.upwork.length !== 1 ? "s" : ""} — Weekly Breakdown</span>
                      <span className="ml-auto text-sm font-semibold text-gray-700">
                        {fmt(data.upwork.reduce((s, u) => s + u.total, 0))}
                      </span>
                    </button>
                    {!isCollapsed && (
                      <>
                        <ReconciliationPanel pmKey="upwork" projectedTotal={data.upwork.reduce((s, u) => s + u.total, 0)} />
                        <div className="space-y-6">
                          {data.upwork.map((u) => <UpworkCard key={u.id} member={u} />)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}
    </div>
  );
}

function ConfirmModal({ title, message, onCancel, onConfirm }: { title: string; message: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
        <h2 className="font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition">Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Margin Tab ───────────────────────────────────────────────────────────────

interface MarginCustomer {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  actualMinutes: number;
  billedAmount: number;
  revenuePerMinute: number | null;
  teamId: string | null;
  teamName: string | null;
  teamColor: string | null;
  regionId: string | null;
  regionName: string | null;
}

interface MarginPcc {
  id: string;
  name: string;
  teamId: string | null;
  teamName: string | null;
  teamColor: string | null;
  regionId: string | null;
  regionName: string | null;
  role: string | null;
  workedHours: number | null;
  billableMinutes: number | null;
  payAmount: number | null;
  costPerBillableMin: number | null;
}

interface MarginResponse {
  customers: MarginCustomer[];
  pccs: MarginPcc[];
  range: {
    startDate: string | null;
    endDate: string | null;
    mode: "custom" | "last_completed_billing_period";
    revenueCalculation: "estimated_single_minimum" | "billing_period";
  };
}

type MarginPreset = "last30" | "last60" | "thisMonth" | "lastMonth" | "custom";

const MARGIN_PRESETS: { value: Exclude<MarginPreset, "custom">; label: string }[] = [
  { value: "last30", label: "Last 30 days" },
  { value: "last60", label: "Last 60 days" },
  { value: "thisMonth", label: "This Month" },
  { value: "lastMonth", label: "Last Month" },
];

function marginPacificToday(): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

function marginShiftDate(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

function marginPresetRange(preset: Exclude<MarginPreset, "custom">): { startDate: string; endDate: string } {
  const today = marginPacificToday();
  const yesterday = marginShiftDate(today, -1);
  // Use today's year/month for This Month start — yesterday may be in the previous month on the 1st
  const [todayYear, todayMonth] = today.split("-").map(Number);
  const [lastYear, lastMonth] = yesterday.split("-").map(Number);
  if (preset === "last30") return { startDate: marginShiftDate(yesterday, -29), endDate: yesterday };
  if (preset === "last60") return { startDate: marginShiftDate(yesterday, -59), endDate: yesterday };
  if (preset === "thisMonth") {
    const startDate = `${todayYear}-${String(todayMonth).padStart(2, "0")}-01`;
    // If today is the 1st, there are no completed days yet in this month — end = yesterday (last month)
    return { startDate, endDate: yesterday };
  }
  const lastMonthEnd = new Date(Date.UTC(lastYear, lastMonth - 1, 0)).toISOString().slice(0, 10);
  return { startDate: lastMonthEnd.slice(0, 8) + "01", endDate: lastMonthEnd };
}

function marginTeamColors(color: string | null): { backgroundColor: string; color: string; boxShadow?: string } {
  const fallback = { backgroundColor: "#f9fafb", color: "#1f2937" };
  const palette: Record<string, string> = {
    peach: "#f2b8a0",
    "pink lighter": "#f7c8d8",
    "pink light": "#f7c8d8",
    "pink a": "#f7c8d8",
    "pink darker": "#e07a9a",
    "pink dark": "#e07a9a",
    "pink b": "#e07a9a",
    yellow: "#f6d365",
    black: "#111827",
    gold: "#d4af37",
    green: "#4caf50",
    grey: "#9ca3af",
    gray: "#9ca3af",
    maroon: "#7a1f1f",
    olive: "#808000",
    orange: "#f97316",
    purple: "#8b5cf6",
    lavender: "#c4b5fd",
    indigo: "#4f46e5",
    red: "#ef4444",
    turquoise: "#14b8a6",
    white: "#ffffff",
  };
  const normalized = color?.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ") ?? "";
  let resolved = palette[normalized] ?? normalized;
  if (/^#[0-9a-f]{3}$/i.test(resolved)) {
    resolved = `#${resolved[1]}${resolved[1]}${resolved[2]}${resolved[2]}${resolved[3]}${resolved[3]}`;
  }
  if (!/^#[0-9a-f]{6}$/i.test(resolved)) return fallback;
  const red = parseInt(resolved.slice(1, 3), 16);
  const green = parseInt(resolved.slice(3, 5), 16);
  const blue = parseInt(resolved.slice(5, 7), 16);
  // Perceived luminance keeps labels readable on both light and dark stored colors.
  const luminance = (red * 299 + green * 587 + blue * 114) / 1000;
  return {
    backgroundColor: resolved,
    color: luminance >= 150 ? "#111827" : "#ffffff",
    ...(luminance >= 240 ? { boxShadow: "inset 0 0 0 1.5px #000000" } : {}),
  };
}

function MarginTab() {
  const initialRange = useMemo(() => marginPresetRange("last30"), []);
  const [data, setData]       = useState<MarginResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [search, setSearch]   = useState("");
  const [preset, setPreset]   = useState<MarginPreset>("last30");
  const [startDate, setStartDate] = useState(initialRange.startDate);
  const [endDate, setEndDate] = useState(initialRange.endDate);
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [collapsedRegions, setCollapsedRegions] = useState<Set<string>>(new Set());
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(new Set());
  const maxDate = useMemo(() => marginShiftDate(marginPacificToday(), -1), []);

  const toggleRegion = (id: string) =>
    setCollapsedRegions((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleTeam = (id: string) =>
    setCollapsedTeams((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams(appliedRange);
    apiFetch(`/cash-flow/margin?${params.toString()}`, { signal: controller.signal })
      .then((response: MarginResponse) => {
        if (active) setData(response);
      })
      .catch((e: unknown) => {
        if (active && !(e instanceof DOMException && e.name === "AbortError")) {
          setError(e instanceof Error ? e.message : "Failed to load margin data");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [appliedRange]);

  const fmtCurrency = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  const fmtDate = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const selectPreset = (nextPreset: Exclude<MarginPreset, "custom">) => {
    const range = marginPresetRange(nextPreset);
    setPreset(nextPreset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setAppliedRange(range);
  };

  const applyCustomRange = () => {
    if (!startDate || !endDate) {
      setError("Select both a start date and an end date.");
      return;
    }
    if (startDate > endDate) {
      setError("Start date must not be after end date.");
      return;
    }
    setPreset("custom");
    setAppliedRange({ startDate, endDate });
  };

  const grouped = useMemo(() => {
    if (!data) return [];
    const query = search.trim().toLowerCase();
    const regionMap = new Map<string, {
      id: string;
      name: string;
      teams: Map<string, {
        id: string;
        name: string;
        color: string | null;
        customers: MarginCustomer[];
        pccs: MarginPcc[];
      }>;
    }>();

    const ensureTeam = (
      regionId: string | null,
      regionName: string | null,
      teamId: string | null,
      teamName: string | null,
    ) => {
      const resolvedRegionId = regionId ?? "__unassigned_region__";
      const resolvedTeamId = teamId ?? `__unassigned_team__:${resolvedRegionId}`;
      if (!regionMap.has(resolvedRegionId)) {
        regionMap.set(resolvedRegionId, {
          id: resolvedRegionId,
          name: regionName ?? "Unassigned Region",
          teams: new Map(),
        });
      }
      const region = regionMap.get(resolvedRegionId)!;
      if (!region.teams.has(resolvedTeamId)) {
        region.teams.set(resolvedTeamId, {
          id: resolvedTeamId,
          name: teamName ?? "Unassigned Team",
          color: null,
          customers: [],
          pccs: [],
        });
      }
      const team = region.teams.get(resolvedTeamId)!;
      return { region, team };
    };

    for (const customer of data.customers) {
      const { team } = ensureTeam(customer.regionId, customer.regionName, customer.teamId, customer.teamName);
      team.color = team.color ?? customer.teamColor;
      team.customers.push(customer);
    }
    for (const pcc of data.pccs) {
      const { team } = ensureTeam(pcc.regionId, pcc.regionName, pcc.teamId, pcc.teamName);
      team.color = team.color ?? pcc.teamColor;
      team.pccs.push(pcc);
    }

    return [...regionMap.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((region) => ({
        ...region,
        teams: [...region.teams.values()]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((team) => {
            const parentMatches = !query ||
              region.name.toLowerCase().includes(query) ||
              team.name.toLowerCase().includes(query);
            return {
              ...team,
              customers: parentMatches
                ? team.customers
                : team.customers.filter((customer) => customer.name.toLowerCase().includes(query)),
              pccs: parentMatches
                ? team.pccs
                : team.pccs.filter((pcc) => pcc.name.toLowerCase().includes(query)),
            };
          })
          .filter((team) => team.customers.length > 0 || team.pccs.length > 0),
      }))
      .filter((region) => region.teams.length > 0);
  }, [data, search]);

  const visibleCustomerCount = grouped.reduce(
    (regionTotal, region) =>
      regionTotal + region.teams.reduce((teamTotal, team) => teamTotal + team.customers.length, 0),
    0,
  );

  const rangeLabel = `${fmtDate(appliedRange.startDate)} – ${fmtDate(appliedRange.endDate)}`;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-4">
          <div>
          <h2 className="text-base font-semibold text-gray-900">Revenue Per Minute by Customer</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {rangeLabel} · grouped by region and team
            </p>
            {preset === "custom" && (
              <p className="text-xs text-amber-600 mt-1">
                Billed amounts for custom ranges are estimates using one configured minimum per account.
              </p>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search accounts, teams, PCCs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 w-64"
            />
          </div>
        </div>

        <div className="px-6 py-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {MARGIN_PRESETS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => selectPreset(option.value)}
                className={`px-3 py-2 text-xs font-medium rounded-lg border transition ${
                  preset === option.value
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-2 ml-auto">
            <label className="text-xs text-gray-500">
              <span className="block mb-1">Start date</span>
              <input
                type="date"
                value={startDate}
                max={maxDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPreset("custom");
                }}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
            <label className="text-xs text-gray-500">
              <span className="block mb-1">End date</span>
              <input
                type="date"
                value={endDate}
                max={maxDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPreset("custom");
                }}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </label>
            <button
              type="button"
              onClick={applyCustomRange}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-10 flex items-center justify-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading margin data…
        </div>
      ) : error ? (
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-8 text-center">
          <p className="text-sm text-red-500">{error}</p>
        </div>
      ) : !data || data.customers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <p className="text-sm text-gray-400">No customer data available.</p>
        </div>
      ) : grouped.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
          <p className="text-sm text-gray-400">No accounts or PCCs match &ldquo;{search}&rdquo;.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <span className="text-xs text-gray-500">
              {visibleCustomerCount} of {data.customers.length} accounts
            </span>
            {loading && (
              <span className="flex items-center gap-1.5 text-xs text-emerald-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Updating…
              </span>
            )}
          </div>
          {grouped.map((region) => {
            const regionCollapsed = collapsedRegions.has(region.id);
            return (
              <section key={region.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleRegion(region.id)}
                  className="w-full px-6 py-3 bg-blue-700 text-white flex items-center justify-between text-left hover:bg-blue-800 transition-colors"
                >
                  <div>
                    <h3 className="font-semibold">{region.name}</h3>
                    <p className="text-xs text-blue-100 mt-0.5">{region.teams.length} team{region.teams.length === 1 ? "" : "s"}</p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-blue-200 shrink-0 transition-transform ${regionCollapsed ? "-rotate-90" : ""}`} />
                </button>
                {!regionCollapsed && (
                  <div className="divide-y divide-gray-200">
                    {region.teams.map((team) => {
                      const teamCollapsed = collapsedTeams.has(team.id);
                      const teamColors = marginTeamColors(team.color);
                      return (
                        <div key={team.id}>
                          <button
                            type="button"
                            onClick={() => toggleTeam(team.id)}
                            className="w-full px-5 py-3 flex items-center justify-between text-left transition-colors hover:brightness-95"
                            style={teamColors}
                          >
                            <div>
                              <h4 className="text-sm font-semibold">{team.name}</h4>
                              <p className="text-xs opacity-75">
                                {team.customers.length} account{team.customers.length === 1 ? "" : "s"} · {team.pccs.length} PCC{team.pccs.length === 1 ? "" : "s"}
                              </p>
                            </div>
                            <ChevronDown
                              className={`h-4 w-4 shrink-0 transition-transform opacity-60 ${teamCollapsed ? "-rotate-90" : ""}`}
                            />
                          </button>
                          {!teamCollapsed && (
                            <>
                              {team.customers.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-gray-100">
                                        <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Customer</th>
                                        <th className="px-5 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Period</th>
                                        <th className="px-5 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Minutes</th>
                                        <th className="px-5 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Est. Billed Amount</th>
                                        <th className="px-5 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Revenue / Min</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                      {team.customers.map((row) => (
                                        <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                                          <td className="px-5 py-3 font-medium text-gray-900">{row.name}</td>
                                          <td className="px-5 py-3 text-gray-500 text-xs whitespace-nowrap">
                                            {fmtDate(row.periodStart)} – {fmtDate(row.periodEnd)}
                                          </td>
                                          <td className="px-5 py-3 text-right text-gray-700 tabular-nums">
                                            {row.actualMinutes > 0
                                              ? row.actualMinutes.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                              : <span className="text-gray-300">—</span>}
                                          </td>
                                          <td className="px-5 py-3 text-right text-gray-700 tabular-nums">
                                            {row.billedAmount > 0 ? fmtCurrency(row.billedAmount) : <span className="text-gray-300">—</span>}
                                          </td>
                                          <td className="px-5 py-3 text-right tabular-nums">
                                            {row.revenuePerMinute !== null ? (
                                              <span className="font-semibold text-emerald-700">${row.revenuePerMinute.toFixed(4)}</span>
                                            ) : (
                                              <span className="text-gray-300">—</span>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="px-5 py-4 text-xs text-gray-400">No active accounts assigned to this team.</p>
                              )}
                              {team.pccs.length > 0 && (
                                <div className="border-t border-gray-100 bg-gray-50">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-gray-100">
                                        <th className="px-5 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">PCC</th>
                                        <th className="px-5 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">Role</th>
                                        <th className="px-5 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wide">Worked Hrs</th>
                                        <th className="px-5 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wide">Billable Mins</th>
                                        <th className="px-5 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wide">Pay</th>
                                        <th className="px-5 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wide">Cost / Billable Min</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {team.pccs
                                        .slice()
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map((pcc) => (
                                          <tr key={pcc.id} className="hover:bg-gray-100 transition-colors">
                                            <td className="px-5 py-2.5 font-medium text-gray-700">{pcc.name}</td>
                                            <td className="px-5 py-2.5 text-xs text-gray-500 capitalize">
                                              {pcc.role === "pcc_incoming" ? "Incoming" : pcc.role === "pcc_outbound" ? "Outbound" : pcc.role ?? "—"}
                                            </td>
                                            <td className="px-5 py-2.5 text-right text-gray-600 tabular-nums text-xs">
                                              {pcc.workedHours !== null ? pcc.workedHours.toFixed(2) : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-5 py-2.5 text-right text-gray-600 tabular-nums text-xs">
                                              {pcc.billableMinutes !== null && pcc.billableMinutes > 0
                                                ? pcc.billableMinutes.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-5 py-2.5 text-right text-gray-700 tabular-nums text-xs">
                                              {pcc.payAmount !== null ? fmtCurrency(pcc.payAmount) : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-5 py-2.5 text-right tabular-nums text-xs">
                                              {pcc.costPerBillableMin !== null
                                                ? <span className="font-medium text-orange-600">${pcc.costPerBillableMin.toFixed(4)}</span>
                                                : <span className="text-gray-300">—</span>}
                                            </td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              {(() => {
                                const hasAnyPcc       = team.pccs.length > 0;
                                const hasAnyCustomer  = team.customers.length > 0;
                                if (!hasAnyPcc && !hasAnyCustomer) return null;

                                const totalRevenue        = team.customers.reduce((s, c) => s + c.billedAmount, 0);
                                const totalAccountMinutes = team.customers.reduce((s, c) => s + c.actualMinutes, 0);
                                // If any PCC has null pay the totals are incomplete — don't show misleading numbers
                                const payIncomplete   = team.pccs.some((p) => p.payAmount === null);
                                const totalPccPay     = payIncomplete ? null : team.pccs.reduce((s, p) => s + (p.payAmount ?? 0), 0);
                                const totalPccMinutes = team.pccs.reduce((s, p) => s + (p.billableMinutes ?? 0), 0);

                                const revenuePerMin  = totalAccountMinutes > 0 ? totalRevenue / totalAccountMinutes : null;
                                const costPerMin     = totalPccPay !== null && totalPccMinutes > 0 ? totalPccPay / totalPccMinutes : null;
                                const netProfit      = totalPccPay !== null ? totalRevenue - totalPccPay : null;
                                const profitPerMin   = revenuePerMin !== null && costPerMin !== null ? revenuePerMin - costPerMin : null;
                                const isNegative     = netProfit !== null && netProfit < 0;

                                return (
                                  <div className={`border-t-2 border-gray-200 px-5 py-3 space-y-2 text-xs ${isNegative ? "bg-red-50" : "bg-emerald-50"}`}>
                                    {payIncomplete && (
                                      <p className="text-amber-600 font-medium">⚠ Some PCCs are missing an hourly rate — pay and profit totals are incomplete.</p>
                                    )}
                                    <div className="flex flex-wrap gap-x-8 gap-y-2">
                                      <div>
                                        <p className="text-gray-400 uppercase tracking-wide font-medium">Est. Revenue</p>
                                        <p className="font-semibold text-gray-800 tabular-nums">{fmtCurrency(totalRevenue)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-400 uppercase tracking-wide font-medium">Total PCC Pay</p>
                                        <p className="font-semibold text-gray-800 tabular-nums">{totalPccPay !== null ? fmtCurrency(totalPccPay) : <span className="text-amber-500">Incomplete</span>}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-400 uppercase tracking-wide font-medium">Est. Net Profit</p>
                                        <p className={`font-semibold tabular-nums ${isNegative ? "text-red-600" : "text-emerald-700"}`}>
                                          {netProfit !== null ? fmtCurrency(netProfit) : <span className="text-amber-500">Incomplete</span>}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-gray-400 uppercase tracking-wide font-medium">Est. Revenue / Min</p>
                                        <p className="font-semibold text-gray-800 tabular-nums">{revenuePerMin !== null ? `$${revenuePerMin.toFixed(4)}` : "—"}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-400 uppercase tracking-wide font-medium">Cost / Min</p>
                                        <p className="font-semibold text-orange-600 tabular-nums">{costPerMin !== null ? `$${costPerMin.toFixed(4)}` : "—"}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-400 uppercase tracking-wide font-medium">Est. Profit / Min</p>
                                        <p className={`font-semibold tabular-nums ${profitPerMin !== null && profitPerMin < 0 ? "text-red-600" : "text-emerald-700"}`}>
                                          {profitPerMin !== null ? `$${profitPerMin.toFixed(4)}` : "—"}
                                        </p>
                                      </div>
                                    </div>
                                    <p className="text-gray-400 italic">Revenue and profit are estimates. Analysis is based on current team assignments, not per-call attribution.</p>
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}

// ─── Commissions Tab ────────────────────────────────────────────────────────

interface CommissionPayRuleForm {
  periodStartDay: number;
  periodEndDay: number;
  payDayOfMonth: number;
  payMonthOffset: 0 | 1;
}

interface SalesPersonData {
  id: string;
  name: string;
  isActive: boolean;
  payFrequency: "monthly" | "twice_monthly";
  payRules: CommissionPayRuleForm[];
}

interface CommissionAssignmentRow {
  customerId: string;
  customerName: string;
  enabled: boolean;
  percentage: number | null;
  assignmentId: string | null;
}

const DEFAULT_MONTHLY_RULE: CommissionPayRuleForm[] = [
  { periodStartDay: 1, periodEndDay: 31, payDayOfMonth: 10, payMonthOffset: 1 },
];
const DEFAULT_TWICE_MONTHLY_RULES: CommissionPayRuleForm[] = [
  { periodStartDay: 1, periodEndDay: 15, payDayOfMonth: 25, payMonthOffset: 0 },
  { periodStartDay: 16, periodEndDay: 31, payDayOfMonth: 10, payMonthOffset: 1 },
];

interface CommissionLine {
  customerId: string;
  customerName: string;
  periodLabel: string;
  payoutDate: string;
  amount: number;
  percentage: number;
}

interface CommissionSalesPersonSummary {
  salesPersonId: string;
  salesPersonName: string;
  totalThisMonth: number;
  practiceCount: number;
  lines: CommissionLine[];
}

interface CommissionSummary {
  year: number;
  month: number;
  totalThisMonth: number;
  bySalesPerson: CommissionSalesPersonSummary[];
}

function CommissionsTab() {
  const [salesPeople, setSalesPeople] = useState<SalesPersonData[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [salesPersonSearch, setSalesPersonSearch] = useState("");

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch("/commissions/sales-people"),
      apiFetch("/commissions/summary"),
    ])
      .then(([spData, summaryData]: [SalesPersonData[], CommissionSummary]) => {
        setSalesPeople(spData);
        setSummary(summaryData);
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed to load sales people"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const selected = salesPeople.find((s) => s.id === selectedId) ?? null;
  const summaryBySalesPersonId = new Map((summary?.bySalesPerson ?? []).map((s) => [s.salesPersonId, s]));
  const monthLabel = summary ? MONTH_NAMES[summary.month - 1] : "";

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Commissions</h2>
          <p className="text-sm text-gray-500">Assign salespeople to practices and set commission percentages</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" /> Add Sales Person
        </button>
      </div>

      {/* Overview strip */}
      {summary && (
        <div className="mb-5 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 px-5 py-4 text-white flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-100">Total Commissions — {monthLabel} {summary.year}</p>
            <p className="text-2xl font-bold mt-0.5">{fmt(summary.totalThisMonth)}</p>
          </div>
          <div className="text-right text-emerald-100 text-xs">
            <p>{salesPeople.length} sales {salesPeople.length === 1 ? "person" : "people"}</p>
            <p>{summary.bySalesPerson.reduce((s, sp) => s + sp.practiceCount, 0)} practice assignments</p>
          </div>
        </div>
      )}

      {showAddForm && (
        <AddSalesPersonForm
          onClose={() => setShowAddForm(false)}
          onCreated={() => {
            setShowAddForm(false);
            loadAll();
          }}
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading sales people…
        </div>
      ) : salesPeople.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-200 rounded-xl">
          <User className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No sales people added yet.</p>
          <p className="text-xs text-gray-400 mt-1">Click "Add Sales Person" to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-[260px_1fr] gap-4 items-start">
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                value={salesPersonSearch}
                onChange={(e) => setSalesPersonSearch(e.target.value)}
                placeholder="Search sales people…"
                className="w-full border border-gray-300 rounded-lg pl-8 pr-2 py-1.5 text-sm bg-white"
              />
            </div>
            {salesPeople
              .filter((sp) => sp.name.toLowerCase().includes(salesPersonSearch.trim().toLowerCase()))
              .length === 0 && (
              <p className="text-xs text-gray-400 text-center py-3">No sales people match "{salesPersonSearch}".</p>
            )}
            {salesPeople
              .filter((sp) => sp.name.toLowerCase().includes(salesPersonSearch.trim().toLowerCase()))
              .map((sp) => {
              const spSummary = summaryBySalesPersonId.get(sp.id);
              const isSelected = selectedId === sp.id;
              return (
                <button
                  key={sp.id}
                  onClick={() => setSelectedId(sp.id)}
                  className={`w-full text-left rounded-lg border px-3.5 py-3 transition-colors ${
                    isSelected
                      ? "border-emerald-400 bg-emerald-50 shadow-sm"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${isSelected ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                        <User className="h-3.5 w-3.5" />
                      </span>
                      <span className={`text-sm font-medium truncate ${isSelected ? "text-emerald-800" : "text-gray-800"}`}>{sp.name}</span>
                    </div>
                    {!sp.isActive && <span className="flex-shrink-0 text-[9px] font-semibold text-gray-400 uppercase bg-gray-100 px-1.5 py-0.5 rounded">Inactive</span>}
                  </div>
                  <div className="flex items-center justify-between mt-2 pl-9">
                    <span className="text-[11px] text-gray-400 flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> {spSummary?.practiceCount ?? 0} {spSummary?.practiceCount === 1 ? "practice" : "practices"}
                    </span>
                    <span className="text-sm font-semibold text-emerald-700">{fmt(spSummary?.totalThisMonth ?? 0)}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <div>
            {!selected ? (
              <div className="text-center py-16 border border-dashed border-gray-200 rounded-xl">
                <p className="text-sm text-gray-500">Select a sales person to manage their assigned practices.</p>
              </div>
            ) : (
              <SalesPersonDetail
                salesPerson={selected}
                summary={summaryBySalesPersonId.get(selected.id) ?? null}
                monthLabel={monthLabel}
                summaryYear={summary?.year ?? new Date().getFullYear()}
                onUpdated={loadAll}
                onDeleted={() => {
                  setSelectedId(null);
                  loadAll();
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PayRulesEditor({
  payRules,
  onChange,
}: {
  payRules: CommissionPayRuleForm[];
  onChange: (rules: CommissionPayRuleForm[]) => void;
}) {
  const updateRule = (index: number, patch: Partial<CommissionPayRuleForm>) => {
    const next = payRules.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {payRules.map((rule, i) => (
        <div key={i} className="grid grid-cols-4 gap-2 items-center text-xs">
          <div>
            <label className="block text-gray-400 mb-0.5">Period start day</label>
            <input
              type="number" min={1} max={31} value={rule.periodStartDay}
              onChange={(e) => updateRule(i, { periodStartDay: Number(e.target.value) })}
              className="w-full border border-gray-300 rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-gray-400 mb-0.5">Period end day</label>
            <input
              type="number" min={1} max={31} value={rule.periodEndDay}
              onChange={(e) => updateRule(i, { periodEndDay: Number(e.target.value) })}
              className="w-full border border-gray-300 rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-gray-400 mb-0.5">Pay day of month</label>
            <input
              type="number" min={1} max={31} value={rule.payDayOfMonth}
              onChange={(e) => updateRule(i, { payDayOfMonth: Number(e.target.value) })}
              className="w-full border border-gray-300 rounded px-2 py-1"
            />
          </div>
          <div>
            <label className="block text-gray-400 mb-0.5">Paid in</label>
            <select
              value={rule.payMonthOffset}
              onChange={(e) => updateRule(i, { payMonthOffset: Number(e.target.value) as 0 | 1 })}
              className="w-full border border-gray-300 rounded px-2 py-1"
            >
              <option value={0}>Same month</option>
              <option value={1}>Following month</option>
            </select>
          </div>
        </div>
      ))}
    </div>
  );
}

function AddSalesPersonForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [payFrequency, setPayFrequency] = useState<"monthly" | "twice_monthly">("monthly");
  const [payRules, setPayRules] = useState<CommissionPayRuleForm[]>(DEFAULT_MONTHLY_RULE);
  const [saving, setSaving] = useState(false);

  const changeFrequency = (freq: "monthly" | "twice_monthly") => {
    setPayFrequency(freq);
    setPayRules(freq === "monthly" ? DEFAULT_MONTHLY_RULE : DEFAULT_TWICE_MONTHLY_RULES);
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      await apiFetch("/commissions/sales-people", {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), payFrequency, payRules }),
      });
      toast.success("Sales person added");
      onCreated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add sales person");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white mb-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">New Sales Person</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
          placeholder="e.g. Amber"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Pay frequency</label>
        <select
          value={payFrequency}
          onChange={(e) => changeFrequency(e.target.value as "monthly" | "twice_monthly")}
          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
        >
          <option value="monthly">Once a month</option>
          <option value="twice_monthly">Twice a month</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Pay schedule</label>
        <PayRulesEditor payRules={payRules} onChange={setPayRules} />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
        <button
          onClick={submit}
          disabled={saving}
          className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Add"}
        </button>
      </div>
    </div>
  );
}

function SalesPersonDetail({
  salesPerson,
  summary,
  monthLabel,
  summaryYear,
  onUpdated,
  onDeleted,
}: {
  salesPerson: SalesPersonData;
  summary: CommissionSalesPersonSummary | null;
  monthLabel: string;
  summaryYear: number;
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const [assignments, setAssignments] = useState<CommissionAssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [practiceSearch, setPracticeSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [draftFrequency, setDraftFrequency] = useState(salesPerson.payFrequency);
  const [draftRules, setDraftRules] = useState<CommissionPayRuleForm[]>(salesPerson.payRules);

  const loadAssignments = useCallback(() => {
    setLoading(true);
    apiFetch(`/commissions/sales-people/${salesPerson.id}/assignments`)
      .then((data: CommissionAssignmentRow[]) => setAssignments(data))
      .catch((e) => toast.error(e instanceof Error ? e.message : "Failed to load practices"))
      .finally(() => setLoading(false));
  }, [salesPerson.id]);

  useEffect(() => {
    loadAssignments();
    setDraftFrequency(salesPerson.payFrequency);
    setDraftRules(salesPerson.payRules);
    setEditingSchedule(false);
  }, [salesPerson.id, salesPerson.payFrequency, salesPerson.payRules, loadAssignments]);

  const addPractice = async (row: CommissionAssignmentRow) => {
    await saveAssignment(row.customerId, true, 1);
    setPracticeSearch("");
    setSearchOpen(false);
  };

  const removePractice = async (row: CommissionAssignmentRow) => {
    await saveAssignment(row.customerId, false, null);
  };

  const saveAssignment = async (customerId: string, enabled: boolean, percentage: number | null) => {
    setAssignments((prev) =>
      prev.map((r) => (r.customerId === customerId ? { ...r, enabled, percentage } : r))
    );
    try {
      await apiFetch(`/commissions/sales-people/${salesPerson.id}/assignments/${customerId}`, {
        method: "PUT",
        body: JSON.stringify({ enabled, percentage }),
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
      loadAssignments();
    }
  };

  const saveSchedule = async () => {
    try {
      await apiFetch(`/commissions/sales-people/${salesPerson.id}`, {
        method: "PUT",
        body: JSON.stringify({ payFrequency: draftFrequency, payRules: draftRules }),
      });
      toast.success("Pay schedule updated");
      setEditingSchedule(false);
      onUpdated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update pay schedule");
    }
  };

  const deleteSalesPerson = async () => {
    if (!window.confirm(`Remove ${salesPerson.name}? This also removes all their practice assignments.`)) return;
    try {
      await apiFetch(`/commissions/sales-people/${salesPerson.id}`, { method: "DELETE" });
      toast.success("Sales person removed");
      onDeleted();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to remove sales person");
    }
  };

  return (
    <div className="space-y-4">
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">{salesPerson.name} — pay schedule</h3>
          <div className="flex items-center gap-3">
            {!editingSchedule && (
              <button onClick={() => setEditingSchedule(true)} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
                <Pencil className="h-3 w-3" /> Edit
              </button>
            )}
            <button onClick={deleteSalesPerson} className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1">
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          </div>
        </div>

        {editingSchedule ? (
          <div className="space-y-3">
            <select
              value={draftFrequency}
              onChange={(e) => {
                const freq = e.target.value as "monthly" | "twice_monthly";
                setDraftFrequency(freq);
                setDraftRules(freq === "monthly" ? DEFAULT_MONTHLY_RULE : DEFAULT_TWICE_MONTHLY_RULES);
              }}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            >
              <option value="monthly">Once a month</option>
              <option value="twice_monthly">Twice a month</option>
            </select>
            <PayRulesEditor payRules={draftRules} onChange={setDraftRules} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditingSchedule(false)} className="px-3 py-1.5 text-sm text-gray-600">Cancel</button>
              <button onClick={saveSchedule} className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded hover:bg-emerald-700">Save</button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <span className="inline-block text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
              {salesPerson.payFrequency === "monthly" ? "Paid once a month" : "Paid twice a month"}
            </span>
            <div className="space-y-1.5">
              {salesPerson.payRules.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                  <CalendarDays className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span className="font-medium">{ordinal(r.periodStartDay)}–{ordinal(r.periodEndDay)}</span>
                  <span className="text-gray-400">of the month, paid the</span>
                  <span className="font-medium">{ordinal(r.payDayOfMonth)}</span>
                  <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                    {r.payMonthOffset === 1 ? "following month" : "same month"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg bg-white">
        <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Commission Breakdown — {monthLabel} {summaryYear}</h3>
          {summary && summary.totalThisMonth > 0 && (
            <span className="text-sm font-semibold text-emerald-700">{fmt(summary.totalThisMonth)}</span>
          )}
        </div>
        {!summary || summary.lines.length === 0 ? (
          <p className="text-sm text-gray-400 px-4 py-3">No commission earned yet for {monthLabel} {summaryYear}.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {summary.lines.map((line, i) => (
              <div key={`${line.customerId}-${line.payoutDate}-${i}`} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <div className="min-w-0">
                  <p className="text-gray-800 font-medium truncate">{line.customerName}</p>
                  <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                    <CalendarDays className="h-3 w-3 flex-shrink-0" />
                    {line.periodLabel}
                    <span className="text-gray-300">•</span>
                    paid {new Date(line.payoutDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    <span className="text-gray-300">•</span>
                    {line.percentage}%
                  </p>
                </div>
                <span className="text-sm font-semibold text-gray-900 flex-shrink-0 ml-3">{fmt(line.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg bg-white">
        <div className="px-4 py-2.5 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Practices</h3>
          <PracticeSearchDropdown
            assignments={assignments}
            search={practiceSearch}
            onSearchChange={setPracticeSearch}
            open={searchOpen}
            onOpenChange={setSearchOpen}
            onSelect={addPractice}
          />
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 p-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading practices…
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {assignments.filter((row) => row.enabled).length === 0 ? (
              <p className="text-sm text-gray-400 px-4 py-3">No practices assigned yet. Search above to add one.</p>
            ) : (
              assignments
                .filter((row) => row.enabled)
                .map((row) => (
                  <div key={row.customerId} className="flex items-center justify-between px-4 py-2 text-sm">
                    <span className="text-gray-700">{row.customerName}</span>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <input
                          type="number" min={0} max={100} step={0.5}
                          value={row.percentage ?? ""}
                          onChange={(e) => saveAssignment(row.customerId, true, Number(e.target.value))}
                          className="w-16 border border-gray-300 rounded px-1.5 py-1 text-xs text-right"
                        />
                        <span className="text-xs text-gray-400">%</span>
                      </div>
                      <button
                        onClick={() => removePractice(row)}
                        className="text-gray-400 hover:text-red-500"
                        title="Remove this practice"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PracticeSearchDropdown({
  assignments,
  search,
  onSearchChange,
  open,
  onOpenChange,
  onSelect,
}: {
  assignments: CommissionAssignmentRow[];
  search: string;
  onSearchChange: (v: string) => void;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSelect: (row: CommissionAssignmentRow) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onOpenChange]);

  const available = assignments.filter((row) => !row.enabled);
  const filtered = search.trim()
    ? available.filter((row) => row.customerName.toLowerCase().includes(search.trim().toLowerCase()))
    : available;

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => onOpenChange(true)}
          placeholder="Search practices to add…"
          className="w-full border border-gray-300 rounded pl-8 pr-2 py-1.5 text-sm"
        />
      </div>
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <p className="text-xs text-gray-400 px-3 py-2">
              {available.length === 0 ? "All practices are already assigned." : "No matching practices."}
            </p>
          ) : (
            filtered.map((row) => (
              <button
                key={row.customerId}
                onClick={() => onSelect(row)}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {row.customerName}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
